import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom, timeout } from 'rxjs';
import * as L from 'leaflet';
import { AuthorizedApiService } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { CompanySelectionService } from '@core/service/company-selection.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { CompanySelectComponent } from '@shared/components/company-select/company-select.component';
import { ConfirmDeleteDialogComponent } from '@shared/components/confirm-delete-dialog/confirm-delete-dialog.component';
import { DateTimePickerFieldComponent } from '@shared/components/date-time-picker-field/date-time-picker-field.component';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { EmployeeRecord } from '../../organization/employees/employee.interface';
import { CompaniesRecord } from '../../organization/companies-records/companies-record.interface';
import {
  TrackingPointRecord,
  TrackingPointsResponse,
  TrackingRouteRecord,
  TrackingRoutesResponse,
} from './tracking-point.interface';

interface EmployeesResponse {
  data?: EmployeeRecord[];
  items?: EmployeeRecord[];
  results?: EmployeeRecord[];
  employees?: EmployeeRecord[];
}
interface CompaniesResponse { items?: CompaniesRecord[]; data?: CompaniesRecord[]; results?: CompaniesRecord[]; companiesRecords?: CompaniesRecord[]; }

interface TrackingTimelinePoint {
  id: string;
  employeeNumber: string;
  trackedAt: string;
  timeLabel: string;
  dateTimeLabel: string;
  coordinatesLabel: string;
  accuracyLabel: string;
  addressSummary: string | null;
  statusLabel: string | null;
  deviceLabel: string | null;
  outsideWorkdayLabel: string | null;
  lat: number;
  lng: number;
  isStart: boolean;
  isEnd: boolean;
}

interface TrackingRouteSummary {
  startLabel: string;
  endLabel: string;
  durationLabel: string;
  distanceLabel: string;
  totalPointsLabel: string;
  totalPoints: number;
}

interface TrackingPointsFilterState {
  activeFilters: Record<string, string | number | boolean | null>;
  dateFrom: string;
  dateTo: string;
  employeeSearchText: string;
  currentPage: number;
  pageSize: number;
}

interface OsrmRouteMatchPoint {
  lat: number;
  lng: number;
  timestamp?: number;
  accuracy?: number;
  speedMps?: number;
  heading?: number;
  headingAccuracy?: number;
  mockLocation?: boolean;
}

type OsrmRoutingProfile = 'driving' | 'foot';
type OsrmRequestType = 'match' | 'route';

interface ProfiledRouteSegment {
  points: TrackingRouteRecord['points'];
  profile: OsrmRoutingProfile;
}

interface OsrmMatchResponse {
  code?: string;
  matchings?: Array<{
    confidence?: number;
    geometry?: {
      type?: string;
      coordinates?: Array<[number, number]>;
    };
  }>;
}

interface OsrmRouteResponse {
  code?: string;
  routes?: Array<{
    geometry?: {
      type?: string;
      coordinates?: Array<[number, number]>;
    };
  }>;
}

interface TrackingDetectedStop {
  lat: number;
  lng: number;
  startedAt: string;
  endedAt: string;
  durationSeconds: number;
  pointCount: number;
}

@Component({
  selector: 'app-tracking-point',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BreadcrumbComponent,
    CompanySelectComponent,
    DateTimePickerFieldComponent,
    MatAutocompleteModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatSelectModule,
    TranslateModule,
  ],
  templateUrl: './tracking-point.component.html',
  styleUrl: './tracking-point.component.scss',
})
export class TrackingPointComponent implements OnInit, AfterViewInit, OnDestroy {
  private static readonly ROUTE_COLORS = ['#2563eb', '#10b981', '#f97316', '#7c3aed', '#ef4444'];
  private static readonly RAW_ROUTE_COLOR = '#2563eb';
  private static readonly PROCESSED_ROUTE_GRADIENT_STOPS = [
    { position: 0, color: '#10b981' },
    { position: 0.38, color: '#14b8a6' },
    { position: 0.72, color: '#f59e0b' },
    { position: 1, color: '#ef4444' },
  ];
  private static readonly PROCESSED_ROUTE_GRADIENT_BANDS = 32;
  private static readonly FILTER_STATE_KEY = 'tracking-points:filters';
  private static readonly LIVE_REFRESH_INTERVAL_MS = 30_000;
  private static readonly OSRM_MATCH_CHUNK_SIZE = 40;
  private static readonly OSRM_REQUEST_TIMEOUT_MS = 8_000;
  private static readonly OSRM_MAX_JUMP_DISTANCE_METERS = 6000;
  private static readonly OSRM_MATCH_MAX_BBOX_DIAGONAL_METERS = 25_000;
  private static readonly ROUTE_SEGMENT_MAX_UNTIMED_DISTANCE_METERS = 1200;
  private static readonly ROUTE_SEGMENT_MAX_SPEED_KMH = 200;
  private static readonly ROUTE_SEGMENT_MAX_TIME_GAP_SECONDS = 180;
  private static readonly SAFE_RAW_FALLBACK_MAX_DISTANCE_METERS = 180;
  private static readonly SAFE_RAW_FALLBACK_MAX_ACCURACY_METERS = 120;
  private static readonly OSRM_POINT_MIN_DISTANCE_METERS = 8;
  private static readonly OSRM_POINT_MIN_TIME_GAP_SECONDS = 20;
  private static readonly OSRM_FALLBACK_ANCHOR_MAX_SPACING_METERS = 650;
  private static readonly OSRM_FALLBACK_ANCHOR_MIN_TURN_DEGREES = 32;
  private static readonly OSRM_FALLBACK_ANCHOR_MIN_DISTANCE_METERS = 40;
  private static readonly OSRM_FALLBACK_ANCHOR_SHARP_TURN_DEGREES = 70;
  private static readonly OSRM_FALLBACK_ANCHOR_SHARP_TURN_MIN_DISTANCE_METERS = 14;
  private static readonly OSRM_MAX_SOURCE_ACCURACY_METERS = 50;
  private static readonly OSRM_MATCH_MIN_RADIUS_METERS = 5;
  private static readonly OSRM_MATCH_MAX_RADIUS_METERS = 50;
  private static readonly OSRM_SHORT_TRANSITION_MAX_POINTS = 5;
  private static readonly OSRM_SHORT_TRANSITION_MAX_DISTANCE_METERS = 420;
  private static readonly OSRM_SHORT_TRANSITION_MAX_DURATION_SECONDS = 90;
  private static readonly OSRM_SHORT_TRANSITION_MIN_PROGRESS_RATIO = 0.55;
  private static readonly OSRM_SHORT_TRANSITION_MIN_DIRECT_DISTANCE_METERS = 25;
  private static readonly OSRM_INFERRED_GAP_MAX_DISTANCE_METERS = 6000;
  private static readonly OSRM_INFERRED_GAP_MAX_DURATION_SECONDS = 3600;
  private static readonly KALMAN_DEFAULT_ACCURACY_METERS = 30;
  private static readonly KALMAN_MIN_ACCURACY_METERS = 5;
  private static readonly KALMAN_MAX_ACCURACY_METERS = 120;
  private static readonly KALMAN_WALKING_NOISE_METERS_PER_SECOND = 3;
  private static readonly KALMAN_CYCLING_NOISE_METERS_PER_SECOND = 7;
  private static readonly KALMAN_MOTORIZED_NOISE_METERS_PER_SECOND = 16;
  private static readonly GPS_SPIKE_MIN_DISTANCE_METERS = 180;
  private static readonly GPS_SPIKE_RETURN_DISTANCE_METERS = 90;
  private static readonly MATCHED_ROUTE_MAX_DISTANCE_FACTOR = 2.4;
  private static readonly MATCHED_ROUTE_MAX_LOCAL_DISTANCE_FACTOR = 1.85;
  private static readonly MATCHED_ROUTE_MAX_LOCAL_EXTRA_METERS = 120;
  private static readonly MATCHED_ROUTE_ENDPOINT_TOLERANCE_METERS = 120;
  private static readonly MATCHED_ROUTE_MAX_SOURCE_OFFSET_METERS = 120;
  private static readonly MATCHED_ROUTE_MIN_SOURCE_OFFSET_METERS = 30;
  private static readonly MATCHED_ROUTE_ORIGINAL_TRACE_MIN_OFFSET_METERS = 45;
  private static readonly MATCHED_ROUTE_ORIGINAL_TRACE_MAX_OFFSET_METERS = 100;
  private static readonly MATCHED_ROUTE_MIN_SOURCE_COVERAGE_RATIO = 0.8;
  private static readonly MATCHED_ROUTE_MIN_ORIGINAL_COVERAGE_RATIO = 0.7;
  private static readonly OSRM_HIGH_CONFIDENCE_MATCH = 0.7;
  private static readonly STOP_RADIUS_METERS = 45;
  private static readonly STOP_MIN_DURATION_SECONDS = 180;
  private static readonly STOP_MAX_SAMPLE_GAP_SECONDS = 180;
  private static readonly STOP_MIN_POINTS = 3;
  private static readonly DIRECTION_ARROW_MIN_SPACING_METERS = 250;
  private static readonly DIRECTION_ARROW_MAX_COUNT = 5;
  private static readonly MAP_DETAIL_LABEL_MIN_ZOOM = 17;
  private static readonly PLAYBACK_SEGMENT_DURATION_MS = 900;
  private static readonly PROFILE_SPEED_WINDOW_SIZE = 5;
  private static readonly PROFILE_SWITCH_CONFIRMATIONS = 3;
  private static readonly PROFILE_FOOT_MAX_SPEED_KMH = 10;
  private static readonly PROFILE_DRIVING_MIN_SPEED_KMH = 16;
  private static readonly PROFILE_INITIAL_FOOT_MAX_SPEED_KMH = 13;
  private static readonly PROFILE_MIN_MOVING_SPEED_KMH = 0.8;
  private static readonly PROFILE_MOTION_MIN_CONFIDENCE = 60;

  @ViewChild('routesMap') private routesMapRef?: ElementRef<HTMLDivElement>;
  @ViewChild('timelineScroller') private timelineScrollerRef?: ElementRef<HTMLDivElement>;
  @ViewChildren('timelineItem') private timelineItemRefs?: QueryList<ElementRef<HTMLElement>>;

  dataSource = new MatTableDataSource<TrackingPointRecord>([]);
  employees: EmployeeRecord[] = [];
  companies: CompaniesRecord[] = [];
  allPoints: TrackingPointRecord[] = [];
  routeGroups: TrackingRouteRecord[] = [];
  timelinePoints: TrackingTimelinePoint[] = [];
  selectedTimelinePointId: string | null = null;
  routeSummary: TrackingRouteSummary = {
    startLabel: '-',
    endLabel: '-',
    durationLabel: '-',
    distanceLabel: '-',
    totalPointsLabel: '0',
    totalPoints: 0,
  };
  isLoading = true;
  isMapLoading = false;
  activeFilters: Record<string, string | number | boolean | null> = {};
  currentPage = 1;
  pageSize = 15;
  totalItems = 0;
  dateFrom = '';
  dateTo = '';
  employeeSearchText: string | EmployeeRecord = '';
  isLiveTracking = false;
  isLegendExpanded = false;
  isFilterPanelExpanded = typeof window === 'undefined' || window.innerWidth > 767;
  showRawRoute = true;
  lastLiveRefreshAt: Date | null = null;
  processedRouteSegments = 0;
  processedRouteFailures = 0;
  processedRoutePending = 0;
  kalmanFallbackSegments = 0;
  detectedStops = 0;
  detectedCuts = 0;
  usedOsrmProfiles = new Set<OsrmRoutingProfile>();
  isPlaybackPlaying = false;
  playbackRate = 1;
  readonly playbackRates = [1, 2, 4];

  get legendTitleLabel(): string {
    return this.translate.currentLang === 'en' || this.translate.currentLang === 'us' ? 'Legend' : 'Leyenda';
  }

  get legendDetailsLabel(): string {
    const isEnglish = this.translate.currentLang === 'en' || this.translate.currentLang === 'us';
    if (this.isLegendExpanded) {
      return isEnglish ? 'Hide details' : 'Ocultar detalles';
    }
    return isEnglish ? 'Show details' : 'Mostrar detalles';
  }

  get osrmProfileLabel(): string {
    if (this.usedOsrmProfiles.has('foot') && this.usedOsrmProfiles.has('driving')) {
      return this.t('trackingPointsCatalog.map.osrmProfilesMixed');
    }
    if (this.usedOsrmProfiles.has('foot')) {
      return this.t('trackingPointsCatalog.map.osrmProfileFoot');
    }
    if (this.usedOsrmProfiles.has('driving')) {
      return this.t('trackingPointsCatalog.map.osrmProfileDriving');
    }
    return this.t('trackingPointsCatalog.map.osrmProfileAuto');
  }
  playbackPosition = 0;
  playbackTimeLabel = '-';
  playbackCurrentSpeedLabel = '-';
  playbackFollowEnabled = true;

  private readonly apiService = inject(AuthorizedApiService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly companySelection = inject(CompanySelectionService);
  private employeeSearchDebounceHandle: ReturnType<typeof setTimeout> | null = null;
  private liveRefreshHandle: ReturnType<typeof setInterval> | null = null;
  private map: L.Map | null = null;
  private tileLayer: L.TileLayer | null = null;
  private routeLayers: L.Layer[] = [];
  private routeMatchRequestId = 0;
  private markerByPointId = new Map<string, L.Marker | L.CircleMarker>();
  private playbackMarker: L.Marker | null = null;
  private playbackAnimationFrame: number | null = null;
  private playbackLastCameraUpdate = 0;
  private playbackLastBearing: number | null = null;
  private mapResizeObserver: ResizeObserver | null = null;
  private processedPlaybackPaths = new Map<string, L.LatLng[]>();
  private playbackProfileByPath = new Map<string, OsrmRoutingProfile>();
  private readonly defaultCenter: L.LatLngTuple = [21.1619, -86.8515];

  ngOnInit(): void {
    this.configureTranslations();
    this.restoreFilterState();
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.configureTranslations();
      this.allPoints = this.allPoints.map((point) => this.normalizePoint(point));
      this.applyLocalFilters();
      this.rebuildRoutePresentation();
      this.renderRoutesOnMap();
    });
    void this.initializeData().finally(() => this.startLiveTracking());
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.renderMap();
      this.observeMapResize();
    }, 0);
  }

  ngOnDestroy(): void {
    this.routeMatchRequestId++;
    this.mapResizeObserver?.disconnect();
    this.mapResizeObserver = null;
    this.stopLiveTracking();
    this.stopPlayback(true);
    this.routeLayers = [];
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  toggleLiveTracking(): void {
    this.isLiveTracking = !this.isLiveTracking;
    if (this.isLiveTracking) {
      this.stopPlayback(true);
      this.startLiveTracking();
      void this.refreshLiveTracking();
      return;
    }
    this.stopLiveTracking();
  }

  private startLiveTracking(): void {
    this.stopLiveTracking();
    if (!this.isLiveTracking || typeof window === 'undefined') {
      return;
    }

    this.liveRefreshHandle = setInterval(() => {
      void this.refreshLiveTracking();
    }, TrackingPointComponent.LIVE_REFRESH_INTERVAL_MS);
  }

  private stopLiveTracking(): void {
    if (this.liveRefreshHandle) {
      clearInterval(this.liveRefreshHandle);
      this.liveRefreshHandle = null;
    }
  }

  private async refreshLiveTracking(): Promise<void> {
    if (
      !this.isLiveTracking
      || !this.canSearchTrackingPoints()
      || this.isLoading
      || this.isMapLoading
      || (typeof document !== 'undefined' && document.hidden)
    ) {
      return;
    }

    await this.refreshAll();
    this.lastLiveRefreshAt = new Date();
    this.focusLatestLivePoint();
  }

  private focusLatestLivePoint(): void {
    if (!this.map || !this.timelinePoints.length) {
      return;
    }

    const latestPoint = this.timelinePoints[this.timelinePoints.length - 1];
    this.map.setView(
      [latestPoint.lat, latestPoint.lng],
      Math.max(this.map.getZoom(), 16),
      { animate: true }
    );
  }

  get selectedEmployeeLabel(): string {
    const employeeId = this.getActiveEmployeeId();
    if (!employeeId) {
      return this.t('trackingPointsCatalog.map.legendAllEmployees');
    }
    return this.employees.find((employee) => employee.employeeId === employeeId)?.fullName || this.t('trackingPointsCatalog.map.legendAllEmployees');
  }

  get hasRouteData(): boolean {
    return this.routeGroups.some((group) => group.points.length > 0);
  }

  get hasTimelineData(): boolean {
    return this.timelinePoints.length > 0;
  }

  get selectedTimelineIndex(): number {
    if (!this.selectedTimelinePointId) {
      return 0;
    }

    const selectedIndex = this.timelinePoints.findIndex((point) => point.id === this.selectedTimelinePointId);
    return selectedIndex >= 0 ? selectedIndex : 0;
  }

  get selectedDateRangeLabel(): string {
    return this.dateFrom || '-';
  }

  get filteredEmployees(): EmployeeRecord[] {
    return this.employees;
  }

  readonly displayEmployeeOption = (value: string | EmployeeRecord | null): string => this.getEmployeeSearchText(value);

  get hasActiveFilters(): boolean {
    return !!this.getActiveCompanyId() || !!this.getActiveEmployeeId() || !!this.dateFrom;
  }

  get hasCompleteSearchCriteria(): boolean {
    return this.canSearchTrackingPoints();
  }

  getEmployeeInitials(employee: EmployeeRecord): string {
    const label = employee.fullName || employee.employeeNumber || '';
    return label.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part.charAt(0)).join('').toUpperCase() || '?';
  }

  async initializeData(): Promise<void> {
    this.isLoading = true;
    try {
      await this.loadCompanies();
      await this.loadEmployees(this.getEmployeeSearchText(this.employeeSearchText));
      if (this.canSearchTrackingPoints()) {
        await this.refreshAll();
      } else {
        this.resetTrackingWorkspace();
      }
    } finally {
      this.isLoading = false;
    }
  }

  async loadData(): Promise<void> {
    if (!this.canSearchTrackingPoints()) {
      this.resetTrackingWorkspace();
      return;
    }

    this.isLoading = true;
    try {
      const response = await firstValueFrom(
        this.apiService.get<TrackingPointsResponse>('attendance/tracking-points', {
          params: {
            page: this.currentPage,
            perPage: this.pageSize,
            ...(this.getActiveCompanyId() ? { companiesRecordId: this.getActiveCompanyId() } : {}),
            ...(this.getActiveEmployeeId() ? { employeeId: this.getActiveEmployeeId() } : {}),
            ...this.buildDateRangeParams(),
          },
        })
      );

      this.totalItems = response?.pagination?.total || 0;
      this.allPoints = (response?.data || []).map((point) => this.normalizePoint(point));
      this.applyLocalFilters();
      this.rebuildRoutePresentation();
      // The route endpoint can finish before point metadata arrives. Render once
      // more so mock-location indicators use the freshly loaded point details.
      this.renderRoutesOnMap();
    } catch (error) {
      console.error('Error loading tracking points', error);
      this.allPoints = [];
      this.dataSource.data = [];
      this.totalItems = 0;
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('trackingPointsCatalog.notifications.loadError')),
        'bottom',
        'center'
      );
    } finally {
      this.isLoading = false;
    }
  }

  async loadRoutes(): Promise<void> {
    if (!this.canSearchTrackingPoints()) {
      this.routeGroups = [];
      this.renderRoutesOnMap();
      this.isMapLoading = false;
      return;
    }

    this.isMapLoading = true;
    try {
      const response = await firstValueFrom(
        this.apiService.get<TrackingRoutesResponse>('attendance/tracking-routes', {
          params: {
            employeeId: this.getActiveEmployeeId(),
            ...(this.getActiveCompanyId() ? { companiesRecordId: this.getActiveCompanyId() } : {}),
            ...this.buildDateRangeParams(),
          },
        })
      );

      this.routeGroups = (response?.data || []).map((group) => ({
        ...group,
        points: [...(group.points || [])].sort((left, right) => left.trackedAt.localeCompare(right.trackedAt)),
      }));
      this.rebuildRoutePresentation();
      this.renderRoutesOnMap();
    } catch (error) {
      console.error('Error loading tracking routes', error);
      this.routeGroups = [];
      this.renderRoutesOnMap();
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('trackingPointsCatalog.notifications.loadRoutesError')),
        'bottom',
        'center'
      );
    } finally {
      this.isMapLoading = false;
    }
  }

  onDateRangeChange(): void {
    this.currentPage = 1;
    void this.refreshAll();
  }

  onEmployeeSearchInput(value: string | EmployeeRecord): void {
    const searchText = this.getEmployeeSearchText(value);
    this.employeeSearchText = value;

    const selectedEmployee = this.employees.find((item) => item.employeeId === this.getActiveEmployeeId());
    if (selectedEmployee && this.getEmployeeSearchText(selectedEmployee) !== searchText.trim()) {
      this.activeFilters = { ...this.activeFilters, employee: null };
    }

    this.persistFilterState();
    this.onFilterSearchChange({ filterId: 'employee', search: searchText });
  }

  onEmployeeSelected(employee: EmployeeRecord | null): void {
    const employeeId = employee?.employeeId || '';
    this.activeFilters = { ...this.activeFilters, employee: employeeId || null };
    this.employeeSearchText = employee || '';
    this.currentPage = 1;
    this.persistFilterState();
    if (!this.canSearchTrackingPoints()) {
      this.resetTrackingWorkspace();
      return;
    }
    void this.refreshAll();
  }

  onCompanyChanged(companyId: string): void {
    this.activeFilters = { ...this.activeFilters, company: companyId || null, employee: null };
    this.employeeSearchText = '';
    this.currentPage = 1;
    this.companySelection.rememberSelection(companyId);
    this.persistFilterState();
    this.resetTrackingWorkspace();
    void this.loadEmployees();
  }

  onDateFromInput(value: string): void {
    this.dateFrom = value || '';
    this.dateTo = this.dateFrom;
    this.activeFilters = {
      ...this.activeFilters,
      dateFrom: this.dateFrom || null,
      dateTo: this.dateFrom || null,
    };
    this.currentPage = 1;
    this.persistFilterState();
    if (!this.canSearchTrackingPoints()) {
      this.resetTrackingWorkspace();
      return;
    }
    if (typeof window !== 'undefined' && window.innerWidth <= 767) {
      this.isFilterPanelExpanded = false;
    }
    void this.refreshAll();
  }

  toggleFilterPanel(): void {
    this.isFilterPanelExpanded = !this.isFilterPanelExpanded;
  }

  shiftSelectedDate(offsetDays: number): void {
    const baseDate = this.dateFrom
      ? new Date(`${this.dateFrom}T12:00:00`)
      : new Date();
    baseDate.setDate(baseDate.getDate() + offsetDays);
    const pad = (value: number) => String(value).padStart(2, '0');
    this.onDateFromInput(`${baseDate.getFullYear()}-${pad(baseDate.getMonth() + 1)}-${pad(baseDate.getDate())}`);
  }

  fitRouteOnMap(): void {
    if (!this.map) {
      return;
    }
    const points = this.routeGroups.flatMap((group) => group.points || []);
    if (!points.length) {
      this.map.setView(this.defaultCenter, 13);
      return;
    }
    const bounds = L.latLngBounds(points.map((point) => [point.lat, point.lng] as L.LatLngTuple));
    this.map.fitBounds(bounds.pad(0.12), { animate: true });
  }

  focusRouteEndpoint(endpoint: 'start' | 'end'): void {
    if (!this.map) {
      return;
    }
    const points = this.routeGroups[0]?.points || [];
    const point = endpoint === 'start' ? points[0] : points[points.length - 1];
    if (point) {
      this.map.setView([point.lat, point.lng], Math.max(this.map.getZoom(), 17), { animate: true });
    }
  }

  toggleRawRoute(): void {
    this.showRawRoute = !this.showRawRoute;
    this.renderRoutesOnMap();
  }

  clearFilters(): void {
    const companyId = this.companies.length === 1 ? this.companies[0].companiesRecordId : '';
    this.activeFilters = companyId ? { company: companyId } : {};
    this.companySelection.rememberSelection(companyId);
    this.dateFrom = '';
    this.dateTo = '';
    this.employeeSearchText = '';
    this.currentPage = 1;
    this.clearPersistedFilterState();
    void this.loadEmployees();
    this.resetTrackingWorkspace();
  }

  openDatePicker(input: HTMLInputElement): void {
    const pickerInput = input as HTMLInputElement & { showPicker?: () => void };
    if (typeof pickerInput.showPicker === 'function') {
      pickerInput.showPicker();
      return;
    }

    input.focus();
    input.click();
  }

  refreshAll(): Promise<void> {
    this.stopPlayback(true);
    if (!this.canSearchTrackingPoints()) {
      this.resetTrackingWorkspace();
      return Promise.resolve();
    }
    return Promise.all([this.loadData(), this.loadRoutes()]).then(() => undefined);
  }

  handleAdd(): void {
    if (!this.hasAccess('create')) {
      return;
    }
    void this.router.navigate(['/attendance/tracking-points/new']);
  }

  async handleView(row: TrackingPointRecord): Promise<void> {
    if (!this.hasAccess('read')) {
      return;
    }
    await this.router.navigate(['/attendance/tracking-points', row.trackingPointId]);
  }

  async handleEdit(row: TrackingPointRecord): Promise<void> {
    if (!this.hasAccess('update')) {
      return;
    }
    await this.router.navigate(['/attendance/tracking-points', row.trackingPointId, 'edit']);
  }

  handleDelete(row: TrackingPointRecord): void {
    if (!this.hasAccess('delete')) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('trackingPointsCatalog.dialog.deleteTitle'),
        message: this.t('trackingPointsCatalog.dialog.deleteMessage', { point: row.employeeNumber }),
        confirmLabel: this.t('trackingPointsCatalog.dialog.deleteConfirm'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.isLoading = true;
      try {
        await firstValueFrom(this.apiService.delete(`attendance/tracking-points/${row.trackingPointId}`));
        await Promise.all([this.loadData(), this.loadRoutes()]);
        this.showNotification('snackbar-success', this.t('trackingPointsCatalog.notifications.deleted'), 'bottom', 'center');
      } catch (error) {
        console.error('Error deleting tracking point', error);
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('trackingPointsCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isLoading = false;
      }
    });
  }

  handleBulkDelete(rows: TrackingPointRecord[]): void {
    if (!this.hasAccess('delete') || !rows.length) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('common.bulkDeleteTitle'),
        message: this.t('common.bulkDeleteMessage', { count: rows.length }),
        confirmLabel: this.t('common.delete'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.isLoading = true;
      try {
        await Promise.all(rows.map((row) => firstValueFrom(this.apiService.delete(`attendance/tracking-points/${row.trackingPointId}`))));
        await Promise.all([this.loadData(), this.loadRoutes()]);
        this.showNotification('snackbar-success', this.t('common.bulkDeleteSuccess', { count: rows.length }), 'bottom', 'center');
      } catch (error) {
        console.error('Error deleting tracking points', error);
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('trackingPointsCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isLoading = false;
      }
    });
  }

  onFiltersChange(filters: Record<string, string | number | boolean | null>): void {
    this.activeFilters = filters;
    this.dateFrom = String(filters['dateFrom'] || '');
    this.dateTo = this.dateFrom;
    this.currentPage = 1;
    this.persistFilterState();
    if (!this.canSearchTrackingPoints()) {
      this.resetTrackingWorkspace();
      return;
    }
    void this.refreshAll();
  }

  onFilterSearchChange(event: { filterId: string; search: string }): void {
    if (event.filterId !== 'employee') {
      return;
    }

    if (this.employeeSearchDebounceHandle) {
      clearTimeout(this.employeeSearchDebounceHandle);
    }

    this.employeeSearchDebounceHandle = setTimeout(() => {
      void this.loadEmployees(event.search);
    }, 250);
  }

  private async loadEmployees(search = ''): Promise<void> {
    try {
      const selectedEmployeeId = this.getActiveEmployeeId();
      const selectedEmployee = this.employees.find((employee) => employee.employeeId === selectedEmployeeId);
      const response = await firstValueFrom(
        this.apiService.get<EmployeeRecord[] | EmployeesResponse>('employees', {
          params: { page: 1, perPage: 20, isActive: 'true', ...(this.getActiveCompanyId() ? { companiesRecordId: this.getActiveCompanyId() } : {}), ...(search ? { search } : {}) },
        })
      );
      const nextEmployees = this.extractEmployees(response)
        .filter((employee) => employee.isActive !== false)
        .sort((left, right) => (left.fullName || '').localeCompare(right.fullName || ''));

      this.employees = selectedEmployee && !nextEmployees.some((employee) => employee.employeeId === selectedEmployee.employeeId)
        ? [selectedEmployee, ...nextEmployees]
        : nextEmployees;

      if (selectedEmployeeId) {
        const refreshedSelectedEmployee = this.employees.find((employee) => employee.employeeId === selectedEmployeeId);
        this.employeeSearchText = refreshedSelectedEmployee || this.getEmployeeSearchText(this.employeeSearchText);
      }
    } catch (error) {
      console.error('Error loading employees for tracking points', error);
      this.employees = [];
    }
  }

  private async loadCompanies(): Promise<void> {
    const previousCompanyId = this.getActiveCompanyId();
    const response = await firstValueFrom(this.apiService.get<CompaniesRecord[] | CompaniesResponse>('companies-records', { params: { isActive: 'true' } }));
    this.companies = (Array.isArray(response) ? response : response.items || response.data || response.results || response.companiesRecords || [])
      .filter((company) => company.isActive !== false)
      .sort((left, right) => left.name.localeCompare(right.name));
    const companyId = this.companySelection.resolveSharedSelection(this.companies, this.getActiveCompanyId());
    this.activeFilters = { ...this.activeFilters, company: companyId || null };
    if (previousCompanyId && previousCompanyId !== companyId) {
      this.activeFilters['employee'] = null;
      this.employeeSearchText = '';
    }
  }

  private extractEmployees(response: EmployeeRecord[] | EmployeesResponse): EmployeeRecord[] {
    if (Array.isArray(response)) {
      return response;
    }
    return response.items || response.data || response.results || response.employees || [];
  }

  private applyLocalFilters(): void {
    this.dataSource.data = [...this.allPoints];
  }

  private normalizePoint(point: Partial<TrackingPointRecord>): TrackingPointRecord {
    const isActive = point.isActive !== false;
    const outsideWorkday = point.outsideWorkday === true;
    const employee = this.employees.find((item) => item.employeeId === point.employeeId);
    const lat = Number(point.lat || 0);
    const lng = Number(point.lng || 0);

    return {
      trackingPointId: point.trackingPointId || point._id || crypto.randomUUID(),
      center: point.center || '',
      employeeId: point.employeeId || '',
      employeeNumber: point.employeeNumber || employee?.employeeNumber || '-',
      companiesRecordId: point.companiesRecordId || '',
      orgUnitId: point.orgUnitId || null,
      orgPath: point.orgPath || null,
      trackedAt: point.trackedAt || '',
      lat,
      lng,
      accuracy: point.accuracy ?? null,
      speedMetersPerSecond: point.speedMetersPerSecond ?? null,
      speedKmh: point.speedKmh ?? null,
      speed: point.speed ?? null,
      heading: point.heading ?? this.firstFiniteNumber([point.rawPayload?.['heading']]),
      motionType: point.motionType || String(point.rawPayload?.['motionType'] || '') || null,
      motionConfidence: point.motionConfidence ?? this.firstFiniteNumber([point.rawPayload?.['motionConfidence']]),
      deviceId: point.deviceId || null,
      deviceVersion: point.deviceVersion || null,
      outsideWorkday,
      mockLocation: point.mockLocation === true || point.rawPayload?.['mockLocation'] === true,
      rawPayload: point.rawPayload || null,
      isActive,
      employeeLabel: employee?.fullName || point.employeeNumber || '-',
      trackedAtLabel: this.formatDateTime(point.trackedAt),
      coordinatesLabel: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
      outsideWorkdayLabel: outsideWorkday ? this.t('common.yes') : this.t('common.no'),
      activeLabel: isActive ? this.t('common.status.active') : this.t('common.status.inactive'),
    };
  }

  private configureTranslations(): void {
    void 0;
  }

  private renderMap(): void {
    const container = this.routesMapRef?.nativeElement;
    if (!container) {
      return;
    }

    if (!this.map) {
      this.map = L.map(container, {
        zoomControl: true,
        attributionControl: true,
      }).setView(this.defaultCenter, 13);

      this.tileLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap contributors',
      });
      this.tileLayer.addTo(this.map);
      this.map.on('zoomend', () => this.updateMapDensityClasses());
    }

    this.renderRoutesOnMap();
    this.updateMapDensityClasses();
    setTimeout(() => this.map?.invalidateSize(), 0);
  }

  private observeMapResize(): void {
    const container = this.routesMapRef?.nativeElement;
    if (!container || typeof ResizeObserver === 'undefined') {
      return;
    }

    this.mapResizeObserver?.disconnect();
    this.mapResizeObserver = new ResizeObserver(() => {
      this.map?.invalidateSize({ animate: false, debounceMoveend: true });
    });
    this.mapResizeObserver.observe(container);
  }

  private renderRoutesOnMap(): void {
    if (!this.map) {
      return;
    }

    for (const layer of this.routeLayers) {
      layer.remove();
    }
    this.routeLayers = [];
    this.markerByPointId.clear();
    this.processedPlaybackPaths.clear();
    this.playbackProfileByPath.clear();
    const routeMatchRequestId = ++this.routeMatchRequestId;
    this.processedRouteSegments = 0;
    this.processedRouteFailures = 0;
    this.processedRoutePending = 0;
    this.kalmanFallbackSegments = 0;
    this.detectedStops = 0;
    this.detectedCuts = 0;
    this.usedOsrmProfiles.clear();

    const bounds = L.latLngBounds([]);

    this.routeGroups.forEach((group, index) => {
      const markerColor = this.getRouteColor(index);
      const points = group.points || [];
      if (!points.length) {
        return;
      }

      const routeSegments = this.splitRouteIntoRenderableSegments(points)
        .flatMap((segment) => this.splitRouteSegmentByProfile(segment));
      const routeSegmentDistances = routeSegments.map((segment) => this.calculatePathDistance(segment.points));
      const totalRenderableDistance = routeSegmentDistances.reduce((total, distance) => total + distance, 0);
      let renderedDistance = 0;

      routeSegments.forEach((segment, segmentIndex) => {
        if (segment.points.length < 2) {
          return;
        }

        const segmentDistance = routeSegmentDistances[segmentIndex];
        const gradientStart = totalRenderableDistance > 0 ? renderedDistance / totalRenderableDistance : 0;
        renderedDistance += segmentDistance;
        const gradientEnd = totalRenderableDistance > 0 ? renderedDistance / totalRenderableDistance : 1;

        const latLngs = segment.points.map((point) => {
          const latLng = L.latLng(point.lat, point.lng);
          bounds.extend(latLng);
          return latLng;
        });

        if (this.showRawRoute) {
          this.drawRoutePolyline(
            latLngs,
            TrackingPointComponent.RAW_ROUTE_COLOR,
            'raw'
          );
        }
        this.processedRoutePending++;
        this.storePlaybackProfile(segment.points, segment.profile);
        void this.replaceRouteWithRoadMatchedPolyline(
          segment.points,
          segment.profile,
          routeMatchRequestId,
          gradientStart,
          gradientEnd
        );
      });

      this.detectedCuts += this.renderRouteCuts(group, this.detectedCuts, routeMatchRequestId);

      points.forEach((point, pointIndex) => {
        const isStart = pointIndex === 0;
        const isEnd = pointIndex === points.length - 1;
        const sourcePoint = this.allPoints.find((item) => item.trackingPointId === point.trackingPointId);
        const marker = isStart || isEnd
          ? L.marker([point.lat, point.lng], {
              icon: this.createEndpointIcon(isStart ? 'start' : 'end', markerColor, point.trackedAt),
              zIndexOffset: 900,
            }).addTo(this.map!)
          : L.circleMarker([point.lat, point.lng], {
              radius: 5,
              color: markerColor,
              weight: 2,
              fillColor: '#ffffff',
              fillOpacity: 1,
              className: 'tracking-route-point-marker',
            }).addTo(this.map!);

        marker.bindPopup(this.buildPointPopup(group, point, isStart, isEnd));
        marker.on('click', () => {
          this.onMapPointSelected(point.trackingPointId);
        });
        marker.on('popupopen', () => {
          void this.hydratePointPopup(group, point, marker, isStart, isEnd);
        });
        this.markerByPointId.set(point.trackingPointId, marker);
        this.routeLayers.push(marker);

        if (this.isMockLocation(point, sourcePoint)) {
          const warningMarker = L.marker([point.lat, point.lng], {
            icon: this.createMockLocationIcon(),
            interactive: false,
            keyboard: false,
            zIndexOffset: 1000,
          }).addTo(this.map!);
          this.routeLayers.push(warningMarker);
        }
      });

      this.detectedStops += this.renderDetectedStops(group);
    });

    if (bounds.isValid()) {
      this.map.fitBounds(bounds.pad(0.12));
    } else {
      this.map.setView(this.defaultCenter, 13);
    }
  }

  private drawRoutePolyline(
    latLngs: L.LatLngExpression[],
    color: string,
    variant: 'raw' | 'processed' = 'processed'
  ): L.Polyline {
    const polyline = L.polyline(latLngs, {
      color,
      weight: variant === 'processed' ? 5 : 3,
      opacity: variant === 'processed' ? 0.92 : 0.62,
      dashArray: variant === 'raw' ? '7 8' : undefined,
    }).addTo(this.map!);
    polyline.bringToBack();

    this.routeLayers.push(polyline);
    return polyline;
  }

  private async replaceRouteWithRoadMatchedPolyline(
    points: TrackingRouteRecord['points'],
    profile: OsrmRoutingProfile,
    requestId: number,
    gradientStart: number,
    gradientEnd: number
  ): Promise<void> {
    const matchPoints = this.toRouteMatchPoints(points);
    if (matchPoints.length < 2) {
      if (requestId === this.routeMatchRequestId) {
        this.processedRoutePending = Math.max(0, this.processedRoutePending - 1);
        this.processedRouteFailures++;
      }
      return;
    }

    try {
      const effectiveProfile = profile;
      const matchedLatLngs = await this.getRoadMatchedRoute(matchPoints, effectiveProfile);
      if (requestId !== this.routeMatchRequestId || !this.map) {
        return;
      }

      const followsOriginalTrace = matchedLatLngs.length >= 2
        && this.hasPlausibleOriginalTraceCoverage(points, matchedLatLngs);

      if (followsOriginalTrace) {
        this.storeProcessedPlaybackPaths(points, matchedLatLngs, effectiveProfile);
        this.drawProcessedRouteGradient(matchedLatLngs, gradientStart, gradientEnd);
        const uncoveredFallbackGroups = this.buildUncoveredRouteMatchFallbackGroups(
          matchPoints,
          matchedLatLngs
        );
        uncoveredFallbackGroups.forEach((fallbackGroup) =>
          this.drawProcessedRouteGradient(fallbackGroup, gradientStart, gradientEnd)
        );
        if (uncoveredFallbackGroups.length) {
          this.kalmanFallbackSegments += uncoveredFallbackGroups.length;
        }
        this.usedOsrmProfiles.add(effectiveProfile);
        this.processedRouteSegments++;
      } else {
        const localFallbackGroups = this.buildSafeRouteMatchFallbackGroups(matchPoints);
        if (localFallbackGroups.length) {
          localFallbackGroups.forEach((fallbackGroup) =>
            this.drawProcessedRouteGradient(fallbackGroup, gradientStart, gradientEnd)
          );
          if (localFallbackGroups.length === 1) {
            this.storeProcessedPlaybackPaths(points, localFallbackGroups[0], profile);
          }
          this.kalmanFallbackSegments += localFallbackGroups.length;
        } else {
          this.processedRouteFailures++;
        }
      }
    } catch (error) {
      if (requestId === this.routeMatchRequestId) {
        this.processedRouteFailures++;
      }
      console.warn('OSRM processing failed for route segment.', error);
    } finally {
      if (requestId === this.routeMatchRequestId) {
        this.processedRoutePending = Math.max(0, this.processedRoutePending - 1);
      }
    }
  }

  private drawProcessedRouteGradient(
    latLngs: L.LatLngExpression[],
    gradientStart: number,
    gradientEnd: number
  ): void {
    if (!this.map || latLngs.length < 2) {
      return;
    }

    const points = latLngs.map((point) => L.latLng(point));
    const segmentDistances = points.slice(1).map((point, index) => points[index].distanceTo(point));
    const totalDistance = segmentDistances.reduce((total, distance) => total + distance, 0);

    if (totalDistance <= 0) {
      return;
    }

    const gradientSpan = Math.max(0, gradientEnd - gradientStart);
    const bandCount = Math.max(
      2,
      Math.ceil(TrackingPointComponent.PROCESSED_ROUTE_GRADIENT_BANDS * gradientSpan)
    );
    const bandPoints = Array.from({ length: bandCount }, () => [] as L.LatLng[]);
    let distanceCovered = 0;

    const drawBand = (band: number, bandPoints: L.LatLng[]): void => {
      if (bandPoints.length < 2) {
        return;
      }

      const localProgress = band / (bandCount - 1);
      const progress = gradientStart + (localProgress * gradientSpan);
      const polyline = L.polyline(bandPoints, {
        color: this.interpolateProcessedRouteColor(progress),
        weight: 6,
        opacity: 0.96,
        lineCap: 'round',
        lineJoin: 'round',
      }).addTo(this.map!);

      this.routeLayers.push(polyline);
    };

    segmentDistances.forEach((distance, index) => {
      if (distance <= 0) {
        return;
      }

      const segmentStartDistance = distanceCovered;
      const segmentEndDistance = distanceCovered + distance;
      let currentDistance = segmentStartDistance;

      while (currentDistance < segmentEndDistance) {
        const progress = currentDistance / totalDistance;
        const band = Math.min(bandCount - 1, Math.floor((progress * bandCount) + 1e-9));
        const bandEndDistance = Math.min(
          segmentEndDistance,
          ((band + 1) / bandCount) * totalDistance
        );
        const fromRatio = (currentDistance - segmentStartDistance) / distance;
        const toRatio = (bandEndDistance - segmentStartDistance) / distance;
        const from = this.interpolateLatLng(points[index], points[index + 1], fromRatio);
        const to = this.interpolateLatLng(points[index], points[index + 1], toRatio);
        const pointsForBand = bandPoints[band];

        if (!pointsForBand.length) {
          pointsForBand.push(from);
        }
        pointsForBand.push(to);
        currentDistance = bandEndDistance;
      }

      distanceCovered += distance;
    });

    bandPoints.forEach((pointsForBand, band) => drawBand(band, pointsForBand));
    this.drawDirectionArrows(points, gradientStart, gradientEnd);
  }

  private storeProcessedPlaybackPaths(
    sourcePoints: TrackingRouteRecord['points'],
    matchedLatLngs: L.LatLngExpression[],
    profile: OsrmRoutingProfile
  ): void {
    if (sourcePoints.length < 2 || matchedLatLngs.length < 2) {
      return;
    }

    const matchedPoints = matchedLatLngs.map((point) => L.latLng(point));
    const matchedIndexes: number[] = [];
    let searchStartIndex = 0;

    sourcePoints.forEach((sourcePoint) => {
      const sourceLatLng = L.latLng(sourcePoint.lat, sourcePoint.lng);
      let nearestIndex = searchStartIndex;
      let nearestDistance = Number.POSITIVE_INFINITY;

      for (let index = searchStartIndex; index < matchedPoints.length; index++) {
        const distance = sourceLatLng.distanceTo(matchedPoints[index]);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      }

      matchedIndexes.push(nearestIndex);
      searchStartIndex = nearestIndex;
    });

    for (let index = 1; index < sourcePoints.length; index++) {
      const fromIndex = matchedIndexes[index - 1];
      const toIndex = matchedIndexes[index];
      if (toIndex <= fromIndex) {
        continue;
      }

      const playbackPathKey = this.getPlaybackPathKey(
        sourcePoints[index - 1].trackingPointId,
        sourcePoints[index].trackingPointId
      );
      this.processedPlaybackPaths.set(
        playbackPathKey,
        matchedPoints.slice(fromIndex, toIndex + 1)
      );
      this.playbackProfileByPath.set(playbackPathKey, profile);
    }
  }

  private storePlaybackProfile(
    sourcePoints: TrackingRouteRecord['points'],
    profile: OsrmRoutingProfile
  ): void {
    for (let index = 1; index < sourcePoints.length; index++) {
      this.playbackProfileByPath.set(
        this.getPlaybackPathKey(sourcePoints[index - 1].trackingPointId, sourcePoints[index].trackingPointId),
        profile
      );
    }
  }

  private getPlaybackPathKey(fromId: string, toId: string): string {
    return `${fromId}::${toId}`;
  }

  private interpolateLatLng(from: L.LatLng, to: L.LatLng, progress: number): L.LatLng {
    return L.latLng(
      from.lat + ((to.lat - from.lat) * progress),
      from.lng + ((to.lng - from.lng) * progress)
    );
  }

  private interpolateProcessedRouteColor(progress: number): string {
    const stops = TrackingPointComponent.PROCESSED_ROUTE_GRADIENT_STOPS;
    const upperIndex = stops.findIndex((stop) => progress <= stop.position);
    const upper = stops[upperIndex < 0 ? stops.length - 1 : upperIndex];
    const lower = stops[Math.max(0, (upperIndex < 0 ? stops.length - 1 : upperIndex) - 1)];

    if (upper.position === lower.position) {
      return upper.color;
    }

    const rangeProgress = (progress - lower.position) / (upper.position - lower.position);
    const lowerRgb = this.hexToRgb(lower.color);
    const upperRgb = this.hexToRgb(upper.color);
    const channel = (from: number, to: number) => Math.round(from + ((to - from) * rangeProgress));

    return `rgb(${channel(lowerRgb.r, upperRgb.r)}, ${channel(lowerRgb.g, upperRgb.g)}, ${channel(lowerRgb.b, upperRgb.b)})`;
  }

  private hexToRgb(color: string): { r: number; g: number; b: number } {
    const normalized = color.replace('#', '');
    return {
      r: Number.parseInt(normalized.slice(0, 2), 16),
      g: Number.parseInt(normalized.slice(2, 4), 16),
      b: Number.parseInt(normalized.slice(4, 6), 16),
    };
  }

  private drawDirectionArrows(
    points: L.LatLng[],
    gradientStart: number,
    gradientEnd: number
  ): void {
    if (!this.map || points.length < 2) {
      return;
    }

    const distances = points.slice(1).map((point, index) => points[index].distanceTo(point));
    const totalDistance = distances.reduce((total, distance) => total + distance, 0);
    if (totalDistance < TrackingPointComponent.DIRECTION_ARROW_MIN_SPACING_METERS * 1.5) {
      return;
    }

    const spacing = Math.max(
      TrackingPointComponent.DIRECTION_ARROW_MIN_SPACING_METERS,
      totalDistance / TrackingPointComponent.DIRECTION_ARROW_MAX_COUNT
    );
    let targetDistance = spacing;
    let coveredDistance = 0;

    distances.forEach((distance, index) => {
      if (distance <= 0) {
        return;
      }

      while (targetDistance < coveredDistance + distance && targetDistance < totalDistance - (spacing * 0.35)) {
        const ratio = (targetDistance - coveredDistance) / distance;
        const position = this.interpolateLatLng(points[index], points[index + 1], ratio);
        const localProgress = targetDistance / totalDistance;
        const routeProgress = gradientStart + (localProgress * (gradientEnd - gradientStart));
        const marker = L.marker(position, {
          icon: this.createDirectionArrowIcon(
            this.calculateBearing(points[index], points[index + 1]),
            this.interpolateProcessedRouteColor(routeProgress)
          ),
          interactive: false,
          keyboard: false,
          zIndexOffset: 500,
        }).addTo(this.map!);
        this.routeLayers.push(marker);
        targetDistance += spacing;
      }

      coveredDistance += distance;
    });
  }

  private calculateBearing(from: L.LatLng, to: L.LatLng): number {
    const fromLat = this.toRadians(from.lat);
    const toLat = this.toRadians(to.lat);
    const deltaLng = this.toRadians(to.lng - from.lng);
    const y = Math.sin(deltaLng) * Math.cos(toLat);
    const x = (Math.cos(fromLat) * Math.sin(toLat))
      - (Math.sin(fromLat) * Math.cos(toLat) * Math.cos(deltaLng));
    return (Math.atan2(y, x) * (180 / Math.PI) + 360) % 360;
  }

  private createDirectionArrowIcon(bearing: number, color: string): L.DivIcon {
    return L.divIcon({
      className: 'tracking-direction-arrow',
      html: `
        <span class="tracking-direction-arrow__badge" style="--arrow-color:${color}">
          <span class="tracking-direction-arrow__glyph" style="transform:rotate(${bearing}deg)">&#9650;</span>
        </span>
      `,
      iconSize: [24, 24],
      iconAnchor: [12, 12],
    });
  }

  private toRouteMatchPoints(points: TrackingRouteRecord['points']): OsrmRouteMatchPoint[] {
    const normalizedPoints = points
      .map((point) => {
        const speedKmh = this.resolvePointSpeedKmh(point);
        return {
          lat: Number(point.lat),
          lng: Number(point.lng),
          timestamp: point.trackedAt ? Math.floor(new Date(point.trackedAt).getTime() / 1000) : undefined,
          accuracy: this.getSourceGpsAccuracy(point.accuracy),
          speedMps: speedKmh !== null ? speedKmh / 3.6 : undefined,
          heading: this.firstFiniteNumber([
            point.heading,
            point.rawPayload?.['heading'],
            point.rawPayload?.['bearing'],
          ]) ?? undefined,
          headingAccuracy: this.firstFiniteNumber([
            point.rawPayload?.['headingAccuracy'],
            point.rawPayload?.['headingAccuracyDegrees'],
          ]) ?? undefined,
          mockLocation: point.mockLocation === true,
        };
      })
      .filter((point) => this.isValidRouteMatchPoint(point));

    const accuratePoints = normalizedPoints.filter((point) =>
      this.getSourceGpsAccuracy(point.accuracy) <= TrackingPointComponent.OSRM_MAX_SOURCE_ACCURACY_METERS
    );
    if (accuratePoints.length < 2) {
      return [];
    }

    const dedupedPoints = this.removeDuplicateRouteMatchPoints(accuratePoints);
    const spikeFilteredPoints = this.removeGpsSpikes(dedupedPoints);
    const jumpFilteredPoints = this.removeAbruptRouteJumps(spikeFilteredPoints);
    const headingFilteredPoints = this.removeHeadingOutliers(jumpFilteredPoints);
    const kalmanFilteredPoints = this.applyAdaptiveKalmanFilter(headingFilteredPoints);
    const compactedPoints = this.compactRouteMatchPoints(kalmanFilteredPoints);
    const trustedPoints = compactedPoints.filter((point) => point.mockLocation !== true);

    if (trustedPoints.length >= 2) {
      return trustedPoints;
    }

    return compactedPoints;
  }

  private async getRoadMatchedRoute(
    points: OsrmRouteMatchPoint[],
    profile: OsrmRoutingProfile
  ): Promise<L.LatLngExpression[]> {
    const chunks = this.chunkRouteMatchPoints(points);
    const matchedRoute: L.LatLngExpression[] = [];

    for (const chunk of chunks) {
      const shouldMatch = this.shouldAttemptOsrmMatch(chunk);
      let matchedChunk = shouldMatch
        ? await this.fetchOsrmMatchedChunk(chunk, profile)
        : [];

      // A single noisy sample or timestamp can make OSRM reject a complete
      // chunk. Retry both overlapping halves so a valid vehicular trace is not
      // reduced to disconnected local fallbacks.
      if (!matchedChunk.length && shouldMatch && chunk.length >= 6) {
        matchedChunk = await this.fetchOsrmMatchedChunkInSegments(chunk, profile);
      }

      // A sparse sample around an intersection may be insufficient for
      // /match even though its endpoints clearly show forward progress. In
      // that narrowly bounded case, /route can infer the legal connector.
      // The progress-ratio guard deliberately excludes near-returning traces,
      // which are the ones that previously produced artificial V/U-turns.
      if (!matchedChunk.length && this.shouldInferShortRoadTransition(chunk)) {
        matchedChunk = await this.fetchOsrmShortTransitionRoute(chunk, profile);
      }

      // On long, fast and mostly straight driving traces, /match may still
      // reject sparse timestamps. A validated /route request using selected
      // anchors preserves road direction and is safer than drawing GPS chords.
      if (!matchedChunk.length && profile === 'driving' && chunk.length >= 2) {
        matchedChunk = await this.fetchOsrmRoutedChunk(chunk, profile);
      }

      if (!matchedChunk.length) {
        // Keep the contiguous route matched up to this point. We deliberately
        // stop here and let the caller render the filtered GPS fallback.
        // Calling /route here would force noisy samples to become mandatory
        // waypoints and can create false U-turns on divided roads.
        break;
      }

      matchedRoute.push(...(matchedRoute.length ? matchedChunk.slice(1) : matchedChunk));
    }

    return matchedRoute;
  }

  private async fetchOsrmMatchedChunk(
    points: OsrmRouteMatchPoint[],
    profile: OsrmRoutingProfile
  ): Promise<L.LatLngExpression[]> {
    if (points.length < 2) {
      return [];
    }

    try {
      const baseParams: Record<string, string> = {
        geometries: 'geojson',
        overview: 'full',
        tidy: 'true',
        gaps: 'ignore',
        radiuses: points.map((point) => String(this.getOsrmMatchRadius(point))).join(';'),
      };
      const match = async (params: Record<string, string>): Promise<L.LatLngExpression[]> => {
        const payload = await firstValueFrom(
          this.apiService.get<OsrmMatchResponse>(this.buildOsrmProxyPath(points, profile, 'match'), { params })
            .pipe(timeout(TrackingPointComponent.OSRM_REQUEST_TIMEOUT_MS))
        );
        if (payload?.code !== 'Ok') {
          return [];
        }

        const matchings = payload.matchings || [];
        const coordinates = matchings.flatMap((matching) => matching.geometry?.coordinates || []);
        if (!coordinates.length) {
          return [];
        }

        const matchedRoute = coordinates.map(([lng, lat]) => [lat, lng] as L.LatLngTuple);
        const confidences = matchings
          .map((matching) => Number(matching.confidence))
          .filter((confidence) => Number.isFinite(confidence));
        const confidence = confidences.length ? Math.min(...confidences) : null;
        return this.isPlausibleMatchedRoute(points, matchedRoute, confidence)
          ? matchedRoute
          : [];
      };

      const hasTimestamps = this.hasStrictlyIncreasingTimestamps(points);
      const timestampedParams = hasTimestamps
        ? {
            ...baseParams,
            timestamps: points.map((point) => String(point.timestamp)).join(';'),
          }
        : baseParams;
      const timestampedMatch = await match(timestampedParams);
      if (timestampedMatch.length || !hasTimestamps) {
        return timestampedMatch;
      }

      // Captures uploaded in batches can contain valid coordinates with small
      // timestamp inconsistencies. Retry spatial matching without timestamps;
      // the resulting geometry still has to pass all plausibility checks.
      return await match(baseParams);
    } catch (error) {
      console.warn('OSRM route matching failed; keeping the filtered GPS trace.', error);
      return [];
    }
  }

  private async fetchOsrmMatchedChunkInSegments(
    points: OsrmRouteMatchPoint[],
    profile: OsrmRoutingProfile
  ): Promise<L.LatLngExpression[]> {
    if (points.length < 2) {
      return [];
    }

    const splitIndex = Math.ceil(points.length / 2);
    const firstSegment = points.slice(0, splitIndex);
    const secondSegment = points.slice(Math.max(splitIndex - 1, 0));

    const [firstMatched, secondMatched] = await Promise.all([
      this.fetchOsrmMatchedChunk(firstSegment, profile),
      this.fetchOsrmMatchedChunk(secondSegment, profile),
    ]);

    if (!firstMatched.length || !secondMatched.length) {
      return [];
    }

    return [...firstMatched, ...secondMatched.slice(1)];
  }

  private async fetchOsrmRoutedChunk(
    points: OsrmRouteMatchPoint[],
    profile: OsrmRoutingProfile
  ): Promise<L.LatLngExpression[]> {
    if (points.length < 2) {
      return [];
    }

    try {
      // Preserve the shape of the trace with a small set of reliable anchors.
      // Using only endpoints loses real turns, while using every GPS sample
      // forces artificial block loops on one-way streets.
      const anchorPoints = this.selectOsrmFallbackAnchors(points);
      const routedChunk = await this.fetchOsrmDirectRoute(anchorPoints, profile);
      return routedChunk.length >= 2
        && this.isPlausibleMatchedRoute(anchorPoints, routedChunk)
        ? routedChunk
        : [];
    } catch (error) {
      console.warn('OSRM route fallback failed, using raw GPS polyline.', error);
      return [];
    }
  }

  private selectOsrmFallbackAnchors(points: OsrmRouteMatchPoint[]): OsrmRouteMatchPoint[] {
    if (points.length <= 2) {
      return points;
    }

    const anchors: OsrmRouteMatchPoint[] = [points[0]];

    for (let index = 1; index < points.length - 1; index++) {
      const previousPoint = points[index - 1];
      const currentPoint = points[index];
      const nextPoint = points[index + 1];
      const distanceFromLastAnchor = this.calculateDistanceInMeters(
        anchors[anchors.length - 1],
        currentPoint
      );
      const incomingBearing = this.calculateBearing(
        L.latLng(previousPoint.lat, previousPoint.lng),
        L.latLng(currentPoint.lat, currentPoint.lng)
      );
      const outgoingBearing = this.calculateBearing(
        L.latLng(currentPoint.lat, currentPoint.lng),
        L.latLng(nextPoint.lat, nextPoint.lng)
      );
      const turnDegrees = Math.abs(((outgoingBearing - incomingBearing + 540) % 360) - 180);
      const isSharpNearbyTurn = turnDegrees >= TrackingPointComponent.OSRM_FALLBACK_ANCHOR_SHARP_TURN_DEGREES
        && distanceFromLastAnchor >= TrackingPointComponent.OSRM_FALLBACK_ANCHOR_SHARP_TURN_MIN_DISTANCE_METERS;
      const isRelevantTurn = turnDegrees >= TrackingPointComponent.OSRM_FALLBACK_ANCHOR_MIN_TURN_DEGREES
        && distanceFromLastAnchor >= TrackingPointComponent.OSRM_FALLBACK_ANCHOR_MIN_DISTANCE_METERS;
      const needsSpacingAnchor = distanceFromLastAnchor
        >= TrackingPointComponent.OSRM_FALLBACK_ANCHOR_MAX_SPACING_METERS;

      if (isSharpNearbyTurn || isRelevantTurn || needsSpacingAnchor) {
        anchors.push(currentPoint);
      }
    }

    const lastPoint = points[points.length - 1];
    if (this.calculateDistanceInMeters(anchors[anchors.length - 1], lastPoint) > 0) {
      anchors.push(lastPoint);
    }

    return anchors;
  }

  private async fetchOsrmRoutedChunkInSegments(
    points: OsrmRouteMatchPoint[],
    profile: OsrmRoutingProfile
  ): Promise<L.LatLngExpression[]> {
    if (points.length < 2) {
      return [];
    }

    const splitIndex = Math.ceil(points.length / 2);
    const firstSegment = points.slice(0, splitIndex);
    const secondSegment = points.slice(Math.max(splitIndex - 1, 0));

    const [firstRouted, secondRouted] = await Promise.all([
      this.fetchOsrmRoutedChunk(firstSegment, profile),
      this.fetchOsrmRoutedChunk(secondSegment, profile),
    ]);

    if (!firstRouted.length || !secondRouted.length) {
      return [];
    }

    return [...firstRouted, ...secondRouted.slice(1)];
  }

  private buildOsrmProxyPath(
    points: OsrmRouteMatchPoint[],
    profile: OsrmRoutingProfile,
    type: OsrmRequestType
  ): string {
    const coordinates = points
      .map((point) => `${point.lng.toFixed(6)},${point.lat.toFixed(6)}`)
      .join(';');
    const mode = profile === 'foot' ? 'walking' : 'driving';
    return `attendance/tracking-routes/proxy/${mode}/${type}/${coordinates}`;
  }

  private async fetchOsrmDirectRoute(
    points: OsrmRouteMatchPoint[],
    profile: OsrmRoutingProfile
  ): Promise<L.LatLngExpression[]> {
    const payload = await firstValueFrom(
      this.apiService.get<OsrmRouteResponse>(this.buildOsrmProxyPath(points, profile, 'route'), {
        params: {
          geometries: 'geojson',
          overview: 'full',
          steps: 'false',
        },
      }).pipe(timeout(TrackingPointComponent.OSRM_REQUEST_TIMEOUT_MS))
    );

    if (payload?.code !== 'Ok') {
      return [];
    }

    const coordinates = payload.routes?.[0]?.geometry?.coordinates || [];
    return coordinates.map(([lng, lat]) => [lat, lng] as L.LatLngTuple);
  }

  private async fetchOsrmShortTransitionRoute(
    points: OsrmRouteMatchPoint[],
    profile: OsrmRoutingProfile
  ): Promise<L.LatLngExpression[]> {
    const endpoints = [points[0], points[points.length - 1]];

    try {
      const routed = await this.fetchOsrmDirectRoute(endpoints, profile);
      return routed.length >= 2 && this.isPlausibleMatchedRoute(endpoints, routed)
        ? routed
        : [];
    } catch (error) {
      console.warn('OSRM could not infer the short road transition.', error);
      return [];
    }
  }

  private shouldInferShortRoadTransition(points: OsrmRouteMatchPoint[]): boolean {
    if (
      points.length < 2
      || points.length > TrackingPointComponent.OSRM_SHORT_TRANSITION_MAX_POINTS
      || points.some((point) => point.mockLocation === true)
    ) {
      return false;
    }

    const pathDistance = this.calculatePathDistance(points);
    const directDistance = this.calculateDistanceInMeters(points[0], points[points.length - 1]);
    if (
      pathDistance <= 0
      || pathDistance > TrackingPointComponent.OSRM_SHORT_TRANSITION_MAX_DISTANCE_METERS
      || directDistance < TrackingPointComponent.OSRM_SHORT_TRANSITION_MIN_DIRECT_DISTANCE_METERS
      || directDistance / pathDistance < TrackingPointComponent.OSRM_SHORT_TRANSITION_MIN_PROGRESS_RATIO
    ) {
      return false;
    }

    const startedAt = points[0].timestamp;
    const endedAt = points[points.length - 1].timestamp;
    if (Number.isFinite(startedAt) && Number.isFinite(endedAt)) {
      const durationSeconds = Number(endedAt) - Number(startedAt);
      if (
        durationSeconds <= 0
        || durationSeconds > TrackingPointComponent.OSRM_SHORT_TRANSITION_MAX_DURATION_SECONDS
      ) {
        return false;
      }
    }

    return points.every((point) =>
      this.getSourceGpsAccuracy(point.accuracy)
        <= TrackingPointComponent.OSRM_MAX_SOURCE_ACCURACY_METERS
    );
  }

  private async fetchOsrmPairwiseRoute(
    points: OsrmRouteMatchPoint[],
    profile: OsrmRoutingProfile
  ): Promise<L.LatLngExpression[]> {
    if (points.length < 2) {
      return [];
    }

    let currentRoute: L.LatLngExpression[] = [];
    let longestRoute: L.LatLngExpression[] = [];

    for (let index = 1; index < points.length; index++) {
      const previousPoint = points[index - 1];
      const currentPoint = points[index];

      const pairRoute = await this.fetchOsrmDirectRoute([previousPoint, currentPoint], profile).catch(() => []);
      if (
        pairRoute.length >= 2
        && this.isPlausibleMatchedRoute([previousPoint, currentPoint], pairRoute)
      ) {
        currentRoute.push(...(currentRoute.length ? pairRoute.slice(1) : pairRoute));
        continue;
      }

      if (currentRoute.length > longestRoute.length) {
        longestRoute = currentRoute;
      }
      currentRoute = [];
    }

    if (currentRoute.length > longestRoute.length) {
      longestRoute = currentRoute;
    }

    return longestRoute.length >= 2 ? longestRoute : [];
  }

  private chunkRouteMatchPoints(points: OsrmRouteMatchPoint[]): OsrmRouteMatchPoint[][] {
    const chunkSize = TrackingPointComponent.OSRM_MATCH_CHUNK_SIZE;
    if (points.length <= chunkSize) {
      return [points];
    }

    const chunks: OsrmRouteMatchPoint[][] = [];
    let start = 0;
    while (start < points.length) {
      const chunk = points.slice(start, start + chunkSize);
      if (chunk.length >= 2) {
        chunks.push(chunk);
      }

      if (start + chunkSize >= points.length) {
        break;
      }
      start += chunkSize - 1;
    }

    return chunks;
  }

  private splitRouteIntoRenderableSegments(points: TrackingRouteRecord['points']): TrackingRouteRecord['points'][] {
    if (points.length <= 1) {
      return points.length ? [points] : [];
    }

    const segments: TrackingRouteRecord['points'][] = [];
    let currentSegment: TrackingRouteRecord['points'] = [points[0]];

    for (let index = 1; index < points.length; index++) {
      const previousPoint = points[index - 1];
      const currentPoint = points[index];

      if (this.shouldStartNewRouteSegment(previousPoint, currentPoint)) {
        if (currentSegment.length >= 2) {
          segments.push(currentSegment);
        }
        currentSegment = [currentPoint];
        continue;
      }

      currentSegment.push(currentPoint);
    }

    if (currentSegment.length >= 2) {
      segments.push(currentSegment);
    }

    return segments;
  }

  private splitRouteSegmentByProfile(points: TrackingRouteRecord['points']): ProfiledRouteSegment[] {
    if (points.length < 2) {
      return [];
    }

    const segmentSpeeds = points
      .map((_, index) => this.resolveProfileSpeedKmh(points, index))
      .filter((speed): speed is number =>
        speed !== null
        && speed >= TrackingPointComponent.PROFILE_MIN_MOVING_SPEED_KMH
        && speed <= TrackingPointComponent.ROUTE_SEGMENT_MAX_SPEED_KMH
      );
    const initialMotionProfile = points
      .slice(0, TrackingPointComponent.PROFILE_SPEED_WINDOW_SIZE)
      .map((point) => this.resolvePointMotionProfile(point))
      .find((profile): profile is OsrmRoutingProfile => profile !== null);
    const baselineProfile: OsrmRoutingProfile = initialMotionProfile
      || (this.median(segmentSpeeds) <= TrackingPointComponent.PROFILE_INITIAL_FOOT_MAX_SPEED_KMH
        ? 'foot'
        : 'driving');
    let activeProfile: OsrmRoutingProfile = baselineProfile;
    const pointProfiles: OsrmRoutingProfile[] = [];
    const speedWindow: number[] = [];
    let pendingProfile: OsrmRoutingProfile | null = null;
    let pendingConfirmations = 0;

    points.forEach((point, index) => {
      const speedKmh = this.resolveProfileSpeedKmh(points, index);
      if (speedKmh !== null && speedKmh >= TrackingPointComponent.PROFILE_MIN_MOVING_SPEED_KMH) {
        speedWindow.push(speedKmh);
        if (speedWindow.length > TrackingPointComponent.PROFILE_SPEED_WINDOW_SIZE) {
          speedWindow.shift();
        }
      }

      const medianSpeed = this.median(speedWindow);
      const motionProfile = this.resolvePointMotionProfile(point);
      const speedCandidate: OsrmRoutingProfile = medianSpeed <= TrackingPointComponent.PROFILE_FOOT_MAX_SPEED_KMH
        ? 'foot'
        : medianSpeed >= TrackingPointComponent.PROFILE_DRIVING_MIN_SPEED_KMH
          ? 'driving'
          : activeProfile;
      // A mostly vehicular segment must not become Foot just because the
      // driver slows down for a turn, traffic light or parking maneuver.
      // An explicit, reliable motion event can still switch it to Foot.
      const candidate = motionProfile
        || (baselineProfile === 'driving' && activeProfile === 'driving'
          ? 'driving'
          : speedCandidate);

      if (candidate !== activeProfile) {
        pendingConfirmations = pendingProfile === candidate ? pendingConfirmations + 1 : 1;
        pendingProfile = candidate;
        if (pendingConfirmations >= TrackingPointComponent.PROFILE_SWITCH_CONFIRMATIONS) {
          activeProfile = candidate;
          pendingProfile = null;
          pendingConfirmations = 0;
        }
      } else {
        pendingProfile = null;
        pendingConfirmations = 0;
      }
      pointProfiles.push(activeProfile);
    });

    const segments: ProfiledRouteSegment[] = [];
    let segmentStart = 0;
    for (let index = 1; index < points.length; index++) {
      if (pointProfiles[index] === pointProfiles[index - 1]) {
        continue;
      }
      segments.push({
        points: points.slice(segmentStart, index + 1),
        profile: pointProfiles[index - 1],
      });
      segmentStart = index;
    }
    segments.push({ points: points.slice(segmentStart), profile: pointProfiles[points.length - 1] });

    return segments.filter((segment) => segment.points.length >= 2);
  }

  private resolveProfileSpeedKmh(
    points: TrackingRouteRecord['points'],
    index: number
  ): number | null {
    const point = points[index];
    const reportedSpeed = this.resolvePointSpeedKmh(point);
    if (reportedSpeed !== null) {
      return reportedSpeed;
    }

    // Some mobile payloads omit speed even though coordinates and timestamps
    // are available. Infer a local speed only for selecting the OSRM profile;
    // the original point data and the speed displayed to the user stay intact.
    const inferredSpeeds: number[] = [];
    const adjacentPairs = [
      index > 0 ? [points[index - 1], point] : null,
      index < points.length - 1 ? [point, points[index + 1]] : null,
    ].filter((pair): pair is [TrackingRouteRecord['points'][number], TrackingRouteRecord['points'][number]] => !!pair);

    adjacentPairs.forEach(([from, to]) => {
      const fromTimestamp = this.resolveTrackingTimestamp(from);
      const toTimestamp = this.resolveTrackingTimestamp(to);
      const elapsedSeconds = this.getElapsedSeconds(fromTimestamp, toTimestamp);
      if (elapsedSeconds <= 0 || elapsedSeconds > TrackingPointComponent.ROUTE_SEGMENT_MAX_TIME_GAP_SECONDS) {
        return;
      }

      const distance = this.calculateDistanceInMeters(from, to);
      const speedKmh = (distance / elapsedSeconds) * 3.6;
      if (Number.isFinite(speedKmh) && speedKmh >= 0 && speedKmh <= TrackingPointComponent.ROUTE_SEGMENT_MAX_SPEED_KMH) {
        inferredSpeeds.push(speedKmh);
      }
    });

    return inferredSpeeds.length ? this.median(inferredSpeeds) : null;
  }

  private resolvePointMotionProfile(point: TrackingRouteRecord['points'][number]): OsrmRoutingProfile | null {
    const sourcePoint = this.allPoints.find((item) => item.trackingPointId === point.trackingPointId);
    const candidates = [point, sourcePoint].filter((item): item is TrackingRouteRecord['points'][number] | TrackingPointRecord => !!item);

    for (const candidate of candidates) {
      const rawPayload = candidate.rawPayload || null;
      const motionType = String(
        candidate.motionType
        ?? rawPayload?.['motionType']
        ?? rawPayload?.['activityType']
        ?? ''
      ).trim().toLowerCase().replace(/[\s-]+/g, '_');
      const confidence = this.firstFiniteNumber([
        candidate.motionConfidence,
        rawPayload?.['motionConfidence'],
        rawPayload?.['activityConfidence'],
      ]);

      if (!motionType || (confidence !== null && confidence < TrackingPointComponent.PROFILE_MOTION_MIN_CONFIDENCE)) {
        continue;
      }

      if (['vehicle', 'in_vehicle', 'automotive', 'car', 'driving', 'motorcycle', 'motorbike'].includes(motionType)) {
        return 'driving';
      }
      if (['foot', 'on_foot', 'walking', 'walk', 'running', 'run'].includes(motionType)) {
        return 'foot';
      }
    }

    return null;
  }

  private resolvePointSpeedKmh(point: TrackingRouteRecord['points'][number]): number | null {
    const sourcePoint = this.allPoints.find((item) => item.trackingPointId === point.trackingPointId);
    const candidates = [point, sourcePoint].filter((item): item is TrackingRouteRecord['points'][number] | TrackingPointRecord => !!item);

    for (const candidate of candidates) {
      const rawPayload = candidate.rawPayload || null;
      const rawCoords = rawPayload?.['coords'];
      const coords = rawCoords && typeof rawCoords === 'object' ? rawCoords as Record<string, unknown> : null;
      const speedKmh = this.firstFiniteNumber([
        candidate.speedKmh,
        rawPayload?.['speedKmh'],
        rawPayload?.['speedKmH'],
        rawPayload?.['velocityKmh'],
        coords?.['speedKmh'],
      ]);
      if (speedKmh !== null && speedKmh >= 0) {
        return speedKmh;
      }

      const speedMetersPerSecond = this.firstFiniteNumber([
        candidate.speedMetersPerSecond,
        candidate.speed,
        rawPayload?.['speedMetersPerSecond'],
        rawPayload?.['speed'],
        coords?.['speedMetersPerSecond'],
        coords?.['speed'],
      ]);
      if (speedMetersPerSecond !== null && speedMetersPerSecond >= 0) {
        return speedMetersPerSecond * 3.6;
      }
    }
    return null;
  }

  private firstFiniteNumber(values: unknown[]): number | null {
    for (const value of values) {
      if (value === null || value === undefined || value === '') {
        continue;
      }
      const numberValue = Number(value);
      if (Number.isFinite(numberValue)) {
        return numberValue;
      }
    }
    return null;
  }

  private median(values: number[]): number {
    if (!values.length) {
      return Number.POSITIVE_INFINITY;
    }
    const sorted = [...values].sort((left, right) => left - right);
    const middle = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
  }

  private renderRouteCuts(
    group: TrackingRouteRecord,
    cutNumberOffset: number,
    requestId: number
  ): number {
    if (!this.map || group.points.length < 2) {
      return 0;
    }

    let cutCount = 0;
    for (let index = 1; index < group.points.length; index++) {
      const previousPoint = group.points[index - 1];
      const currentPoint = group.points[index];
      if (!this.shouldStartNewRouteSegment(previousPoint, currentPoint)) {
        continue;
      }

      const from = L.latLng(previousPoint.lat, previousPoint.lng);
      const to = L.latLng(currentPoint.lat, currentPoint.lng);
      const distance = from.distanceTo(to);
      const cutId = `C${cutNumberOffset + cutCount + 1}`;
      const durationLabel = this.formatDuration(previousPoint.trackedAt, currentPoint.trackedAt);
      const distanceLabel = this.formatDistance(distance);
      const connector = L.polyline([from, to], {
        color: '#64748b',
        weight: 3,
        opacity: 0.7,
        dashArray: '2 10',
        lineCap: 'round',
        interactive: false,
      }).addTo(this.map);
      connector.bringToBack();

      const buildPopup = (): string => this.buildRouteCutPopup(
        group,
        previousPoint,
        currentPoint,
        cutId,
        durationLabel,
        distanceLabel
      );

      const fromMarker = L.marker(from, {
        icon: this.createRouteCutEndpointIcon('from', cutId),
        zIndexOffset: 820,
      }).addTo(this.map);
      const toMarker = L.marker(to, {
        icon: this.createRouteCutEndpointIcon('to', cutId),
        zIndexOffset: 820,
      }).addTo(this.map);

      fromMarker.bindPopup(buildPopup());
      toMarker.bindPopup(buildPopup());
      const hydratePopup = (): void => {
        void this.hydrateRouteCutPopup(group, previousPoint, currentPoint, fromMarker, toMarker, buildPopup);
      };
      fromMarker.on('click', hydratePopup);
      toMarker.on('click', hydratePopup);

      this.routeLayers.push(connector, fromMarker, toMarker);
      void this.renderInferredRouteCut(
        previousPoint,
        currentPoint,
        requestId,
        index / Math.max(1, group.points.length - 1)
      );
      cutCount++;
    }

    return cutCount;
  }

  private async renderInferredRouteCut(
    previousPoint: TrackingRouteRecord['points'][number],
    currentPoint: TrackingRouteRecord['points'][number],
    requestId: number,
    progress: number
  ): Promise<void> {
    if (previousPoint.mockLocation === true || currentPoint.mockLocation === true) {
      return;
    }

    const distance = this.calculateDistanceInMeters(previousPoint, currentPoint);
    const previousTimestamp = previousPoint.trackedAt ? new Date(previousPoint.trackedAt).getTime() : null;
    const currentTimestamp = currentPoint.trackedAt ? new Date(currentPoint.trackedAt).getTime() : null;
    const elapsedSeconds = previousTimestamp && currentTimestamp && currentTimestamp > previousTimestamp
      ? (currentTimestamp - previousTimestamp) / 1000
      : 0;
    if (
      distance <= 0
      || distance > TrackingPointComponent.OSRM_INFERRED_GAP_MAX_DISTANCE_METERS
      || elapsedSeconds > TrackingPointComponent.OSRM_INFERRED_GAP_MAX_DURATION_SECONDS
    ) {
      return;
    }

    const endpoints: OsrmRouteMatchPoint[] = [previousPoint, currentPoint].map((point) => ({
      lat: Number(point.lat),
      lng: Number(point.lng),
      timestamp: point.trackedAt ? Math.floor(new Date(point.trackedAt).getTime() / 1000) : undefined,
      accuracy: this.getSourceGpsAccuracy(point.accuracy),
      speedMps: (this.resolvePointSpeedKmh(point) ?? 0) / 3.6,
      mockLocation: point.mockLocation === true,
    }));
    if (!endpoints.every((point) => this.isValidRouteMatchPoint(point))) {
      return;
    }

    const profile = this.splitRouteSegmentByProfile([previousPoint, currentPoint])[0]?.profile || 'driving';
    try {
      const routed = await this.fetchOsrmDirectRoute(endpoints, profile);
      if (
        requestId !== this.routeMatchRequestId
        || !this.map
        || routed.length < 2
        || !this.isPlausibleMatchedRoute(endpoints, routed)
      ) {
        return;
      }

      const gradientEnd = Math.min(1, progress + 0.03);
      this.drawProcessedRouteGradient(routed, progress, gradientEnd);
      this.usedOsrmProfiles.add(profile);
    } catch (error) {
      console.warn('OSRM could not infer the road connector across a tracking gap.', error);
    }
  }

  private resolveRouteCutReason(
    previousPoint: TrackingRouteRecord['points'][number],
    currentPoint: TrackingRouteRecord['points'][number]
  ): string {
    if (previousPoint.mockLocation === true || currentPoint.mockLocation === true) {
      return this.t('trackingPointsCatalog.map.cutMockLocation');
    }

    const previousTimestamp = new Date(previousPoint.trackedAt).getTime();
    const currentTimestamp = new Date(currentPoint.trackedAt).getTime();
    if (Number.isFinite(previousTimestamp) && Number.isFinite(currentTimestamp)) {
      const elapsedSeconds = (currentTimestamp - previousTimestamp) / 1000;
      if (elapsedSeconds > TrackingPointComponent.ROUTE_SEGMENT_MAX_TIME_GAP_SECONDS) {
        return this.t('trackingPointsCatalog.map.cutNoData');
      }
    }

    return this.t('trackingPointsCatalog.map.cutGpsJump');
  }

  private createRouteCutEndpointIcon(
    type: 'from' | 'to',
    cutId: string
  ): L.DivIcon {
    return L.divIcon({
      className: 'tracking-route-cut-endpoint',
      html: `<span class="tracking-route-cut-endpoint__badge tracking-route-cut-endpoint__badge--${type}">${cutId}</span>`,
      iconSize: [30, 30],
      iconAnchor: [15, 15],
      popupAnchor: [0, -17],
    });
  }

  private updateMapDensityClasses(): void {
    const container = this.routesMapRef?.nativeElement;
    if (!container || !this.map) {
      return;
    }
    container.classList.toggle(
      'tracking-points-routes__map--detail-labels',
      this.map.getZoom() >= TrackingPointComponent.MAP_DETAIL_LABEL_MIN_ZOOM
    );
  }

  private renderDetectedStops(group: TrackingRouteRecord): number {
    if (!this.map) {
      return 0;
    }

    const stops = this.detectStops(group.points);
    stops.forEach((stop, index) => {
      const marker = L.marker([stop.lat, stop.lng], {
        icon: this.createStopIcon(index + 1),
        zIndexOffset: 700,
      }).addTo(this.map!);
      marker.bindPopup(`
        <div class="tracking-route-popup">
          <span class="tracking-route-popup__tag tracking-route-popup__tag--stop">${this.t('trackingPointsCatalog.map.stopTitle')} ${index + 1}</span>
          <strong>${this.formatDuration(stop.startedAt, stop.endedAt)}</strong>
          <span>${this.t('trackingPointsCatalog.map.stopArrival')}: ${this.formatDateTime(stop.startedAt)}</span>
          <span>${this.t('trackingPointsCatalog.map.stopDeparture')}: ${this.formatDateTime(stop.endedAt)}</span>
          <span>${this.t('trackingPointsCatalog.map.stopSamples')}: ${stop.pointCount}</span>
        </div>
      `);
      this.routeLayers.push(marker);
    });

    return stops.length;
  }

  private detectStops(points: TrackingRouteRecord['points']): TrackingDetectedStop[] {
    if (points.length < TrackingPointComponent.STOP_MIN_POINTS) {
      return [];
    }

    const stops: TrackingDetectedStop[] = [];
    let candidate: TrackingRouteRecord['points'] = [points[0]];

    const finalizeCandidate = (): void => {
      if (candidate.length < TrackingPointComponent.STOP_MIN_POINTS) {
        return;
      }

      const first = candidate[0];
      const last = candidate[candidate.length - 1];
      const startedAt = new Date(first.trackedAt).getTime();
      const endedAt = new Date(last.trackedAt).getTime();
      const durationSeconds = (endedAt - startedAt) / 1000;
      if (!Number.isFinite(durationSeconds) || durationSeconds < TrackingPointComponent.STOP_MIN_DURATION_SECONDS) {
        return;
      }

      stops.push({
        lat: candidate.reduce((total, point) => total + point.lat, 0) / candidate.length,
        lng: candidate.reduce((total, point) => total + point.lng, 0) / candidate.length,
        startedAt: first.trackedAt,
        endedAt: last.trackedAt,
        durationSeconds,
        pointCount: candidate.length,
      });
    };

    for (let index = 1; index < points.length; index++) {
      const currentPoint = points[index];
      const previousPoint = points[index - 1];
      const centroid = {
        lat: candidate.reduce((total, point) => total + point.lat, 0) / candidate.length,
        lng: candidate.reduce((total, point) => total + point.lng, 0) / candidate.length,
      };
      const distanceFromCentroid = this.calculateDistanceInMeters(centroid, currentPoint);
      const previousTimestamp = new Date(previousPoint.trackedAt).getTime();
      const currentTimestamp = new Date(currentPoint.trackedAt).getTime();
      const sampleGapSeconds = (currentTimestamp - previousTimestamp) / 1000;
      const continuesCandidate = distanceFromCentroid <= TrackingPointComponent.STOP_RADIUS_METERS
        && Number.isFinite(sampleGapSeconds)
        && sampleGapSeconds > 0
        && sampleGapSeconds <= TrackingPointComponent.STOP_MAX_SAMPLE_GAP_SECONDS;

      if (continuesCandidate) {
        candidate.push(currentPoint);
        continue;
      }

      finalizeCandidate();
      candidate = [currentPoint];
    }

    finalizeCandidate();
    return stops;
  }

  private createStopIcon(index: number): L.DivIcon {
    return L.divIcon({
      className: 'tracking-stop-marker',
      html: `<span class="tracking-stop-marker__badge"><span class="tracking-stop-marker__pause">&#10074;&#10074;</span><small>${index}</small></span>`,
      iconSize: [38, 38],
      iconAnchor: [19, 19],
      popupAnchor: [0, -20],
    });
  }

  private shouldStartNewRouteSegment(previousPoint: TrackingRouteRecord['points'][number], currentPoint: TrackingRouteRecord['points'][number]): boolean {
    if (previousPoint.mockLocation === true || currentPoint.mockLocation === true) {
      return true;
    }

    const previousTimestamp = previousPoint.trackedAt ? new Date(previousPoint.trackedAt).getTime() : null;
    const currentTimestamp = currentPoint.trackedAt ? new Date(currentPoint.trackedAt).getTime() : null;
    const distance = this.calculateDistanceInMeters(
      { lat: Number(previousPoint.lat), lng: Number(previousPoint.lng) },
      { lat: Number(currentPoint.lat), lng: Number(currentPoint.lng) }
    );

    if (!previousTimestamp || !currentTimestamp || currentTimestamp <= previousTimestamp) {
      return distance > TrackingPointComponent.ROUTE_SEGMENT_MAX_UNTIMED_DISTANCE_METERS;
    }

    const elapsedSeconds = Math.floor((currentTimestamp - previousTimestamp) / 1000);
    if (elapsedSeconds > TrackingPointComponent.ROUTE_SEGMENT_MAX_TIME_GAP_SECONDS) {
      return true;
    }

    const speedKmh = elapsedSeconds > 0 ? (distance / elapsedSeconds) * 3.6 : 0;
    return speedKmh > TrackingPointComponent.ROUTE_SEGMENT_MAX_SPEED_KMH;
  }

  private isValidRouteMatchPoint(point: OsrmRouteMatchPoint): boolean {
    return Number.isFinite(point.lat)
      && Number.isFinite(point.lng)
      && point.lat >= -90
      && point.lat <= 90
      && point.lng >= -180
      && point.lng <= 180;
  }

  private removeDuplicateRouteMatchPoints(points: OsrmRouteMatchPoint[]): OsrmRouteMatchPoint[] {
    if (points.length <= 1) {
      return points;
    }

    const filtered: OsrmRouteMatchPoint[] = [points[0]];

    for (let index = 1; index < points.length; index++) {
      const currentPoint = points[index];
      const previousPoint = filtered[filtered.length - 1];
      const distance = this.calculateDistanceInMeters(previousPoint, currentPoint);
      const sameTimestamp = previousPoint.timestamp && currentPoint.timestamp
        ? previousPoint.timestamp === currentPoint.timestamp
        : false;
      const elapsedSeconds = this.getElapsedSeconds(previousPoint.timestamp, currentPoint.timestamp);
      const duplicateRadius = Math.min(
        5,
        Math.max(
          1.5,
          Math.min(
            this.normalizeGpsAccuracy(previousPoint.accuracy),
            this.normalizeGpsAccuracy(currentPoint.accuracy)
          ) * 0.5
        )
      );
      const isNearbyDuplicate = elapsedSeconds > 0
        && elapsedSeconds <= 3
        && distance <= duplicateRadius;

      if (isNearbyDuplicate || sameTimestamp) {
        const previousAccuracy = this.normalizeGpsAccuracy(previousPoint.accuracy);
        const currentAccuracy = this.normalizeGpsAccuracy(currentPoint.accuracy);
        if (currentAccuracy <= previousAccuracy) {
          filtered[filtered.length - 1] = currentPoint;
        }
        continue;
      }

      filtered.push(currentPoint);
    }

    return filtered;
  }

  private removeHeadingOutliers(points: OsrmRouteMatchPoint[]): OsrmRouteMatchPoint[] {
    if (points.length < 3) {
      return points;
    }

    const filtered: OsrmRouteMatchPoint[] = [points[0]];
    for (let index = 1; index < points.length - 1; index++) {
      const previousPoint = filtered[filtered.length - 1];
      const currentPoint = points[index];
      const nextPoint = points[index + 1];
      const heading = Number(currentPoint.heading);
      const headingAccuracy = Number(currentPoint.headingAccuracy);
      const speedMps = Number(currentPoint.speedMps);
      const displacement = this.calculateDistanceInMeters(previousPoint, currentPoint);
      const hasReliableHeading = Number.isFinite(heading)
        && (!Number.isFinite(headingAccuracy) || headingAccuracy <= 30)
        && Number.isFinite(speedMps)
        && speedMps >= 1.5
        && displacement >= Math.max(
          8,
          this.normalizeGpsAccuracy(currentPoint.accuracy) * 1.5
        );

      if (!hasReliableHeading) {
        filtered.push(currentPoint);
        continue;
      }

      const incomingBearing = this.calculateBearing(
        L.latLng(previousPoint.lat, previousPoint.lng),
        L.latLng(currentPoint.lat, currentPoint.lng)
      );
      const outgoingBearing = this.calculateBearing(
        L.latLng(currentPoint.lat, currentPoint.lng),
        L.latLng(nextPoint.lat, nextPoint.lng)
      );
      const incomingDelta = this.calculateBearingDelta(heading, incomingBearing);
      const outgoingDelta = this.calculateBearingDelta(heading, outgoingBearing);

      if (incomingDelta <= 100 || outgoingDelta <= 100) {
        filtered.push(currentPoint);
      }
    }

    filtered.push(points[points.length - 1]);
    return filtered.length >= 2 ? filtered : points;
  }

  private calculateBearingDelta(left: number, right: number): number {
    return Math.abs(((left - right + 540) % 360) - 180);
  }

  private removeAbruptRouteJumps(points: OsrmRouteMatchPoint[]): OsrmRouteMatchPoint[] {
    if (points.length <= 2) {
      return points;
    }

    const filtered: OsrmRouteMatchPoint[] = [points[0]];

    for (let index = 1; index < points.length; index++) {
      const currentPoint = points[index];
      const previousPoint = filtered[filtered.length - 1];
      const distance = this.calculateDistanceInMeters(previousPoint, currentPoint);

      // Keep explicit mock points out of the matched trace when possible.
      if (currentPoint.mockLocation === true && index < points.length - 1) {
        continue;
      }

      if (distance <= TrackingPointComponent.OSRM_MAX_JUMP_DISTANCE_METERS || index === points.length - 1) {
        filtered.push(currentPoint);
      }
    }

    return filtered.length >= 2 ? filtered : points;
  }

  private removeGpsSpikes(points: OsrmRouteMatchPoint[]): OsrmRouteMatchPoint[] {
    if (points.length < 3) {
      return points;
    }

    const filtered: OsrmRouteMatchPoint[] = [points[0]];
    for (let index = 1; index < points.length - 1; index++) {
      const previousPoint = filtered[filtered.length - 1];
      const currentPoint = points[index];
      const nextPoint = points[index + 1];
      const distanceFromPrevious = this.calculateDistanceInMeters(previousPoint, currentPoint);
      const distanceToNext = this.calculateDistanceInMeters(currentPoint, nextPoint);
      const returnDistance = this.calculateDistanceInMeters(previousPoint, nextPoint);
      const elapsedSeconds = this.getElapsedSeconds(previousPoint.timestamp, currentPoint.timestamp);
      const impliedSpeedKmh = elapsedSeconds > 0 ? (distanceFromPrevious / elapsedSeconds) * 3.6 : 0;

      const isIsolatedSpike = distanceFromPrevious >= TrackingPointComponent.GPS_SPIKE_MIN_DISTANCE_METERS
        && distanceToNext >= TrackingPointComponent.GPS_SPIKE_MIN_DISTANCE_METERS
        && returnDistance <= TrackingPointComponent.GPS_SPIKE_RETURN_DISTANCE_METERS;
      const isImpossibleJump = elapsedSeconds > 0
        && impliedSpeedKmh > TrackingPointComponent.ROUTE_SEGMENT_MAX_SPEED_KMH
        && returnDistance < distanceFromPrevious * 0.55;

      if (!isIsolatedSpike && !isImpossibleJump && currentPoint.mockLocation !== true) {
        filtered.push(currentPoint);
      }
    }

    filtered.push(points[points.length - 1]);
    return filtered;
  }

  private applyAdaptiveKalmanFilter(points: OsrmRouteMatchPoint[]): OsrmRouteMatchPoint[] {
    if (points.length < 2) {
      return points;
    }

    const firstPoint = points[0];
    let estimatedLat = firstPoint.lat;
    let estimatedLng = firstPoint.lng;
    let estimateVariance = Math.pow(
      this.normalizeGpsAccuracy(firstPoint.accuracy),
      2
    );
    const processNoiseMetersPerSecond = this.resolveKalmanProcessNoise(points);
    let previousTimestamp = firstPoint.timestamp;
    const filtered: OsrmRouteMatchPoint[] = [{ ...firstPoint }];

    for (let index = 1; index < points.length; index++) {
      const point = points[index];
      const elapsedSeconds = Math.max(
        1,
        Math.min(this.getElapsedSeconds(previousTimestamp, point.timestamp) || 1, 120)
      );
      estimateVariance += processNoiseMetersPerSecond
        * processNoiseMetersPerSecond
        * elapsedSeconds;

      const measurementAccuracy = this.normalizeGpsAccuracy(point.accuracy);
      const measurementVariance = measurementAccuracy * measurementAccuracy;
      const kalmanGain = estimateVariance / (estimateVariance + measurementVariance);

      estimatedLat += kalmanGain * (point.lat - estimatedLat);
      estimatedLng += kalmanGain * (point.lng - estimatedLng);
      estimateVariance = Math.max(1, (1 - kalmanGain) * estimateVariance);
      previousTimestamp = point.timestamp ?? previousTimestamp;

      filtered.push({
        ...point,
        lat: estimatedLat,
        lng: estimatedLng,
        // Keep the device accuracy for OSRM radiuses. Kalman covariance is an
        // internal uncertainty estimate, not a replacement for GPS accuracy.
        accuracy: point.accuracy,
      });
    }

    return filtered;
  }

  private resolveKalmanProcessNoise(points: OsrmRouteMatchPoint[]): number {
    const speedsMetersPerSecond = points.slice(1)
      .map((point, index) => {
        const previousPoint = points[index];
        const elapsedSeconds = this.getElapsedSeconds(previousPoint.timestamp, point.timestamp);
        if (elapsedSeconds <= 0 || elapsedSeconds > TrackingPointComponent.ROUTE_SEGMENT_MAX_TIME_GAP_SECONDS) {
          return null;
        }
        return this.calculateDistanceInMeters(previousPoint, point) / elapsedSeconds;
      })
      .filter((speed): speed is number => speed !== null && Number.isFinite(speed))
      .sort((left, right) => left - right);

    if (!speedsMetersPerSecond.length) {
      return TrackingPointComponent.KALMAN_CYCLING_NOISE_METERS_PER_SECOND;
    }

    const medianSpeed = speedsMetersPerSecond[Math.floor(speedsMetersPerSecond.length / 2)];
    if (medianSpeed <= 3.5) {
      return TrackingPointComponent.KALMAN_WALKING_NOISE_METERS_PER_SECOND;
    }
    if (medianSpeed <= 12) {
      return TrackingPointComponent.KALMAN_CYCLING_NOISE_METERS_PER_SECOND;
    }
    return TrackingPointComponent.KALMAN_MOTORIZED_NOISE_METERS_PER_SECOND;
  }

  private compactRouteMatchPoints(points: OsrmRouteMatchPoint[]): OsrmRouteMatchPoint[] {
    if (points.length <= 2) {
      return points;
    }

    const compacted: OsrmRouteMatchPoint[] = [points[0]];

    for (let index = 1; index < points.length - 1; index++) {
      const currentPoint = points[index];
      const lastPoint = compacted[compacted.length - 1];
      const distance = this.calculateDistanceInMeters(lastPoint, currentPoint);
      const elapsedSeconds = this.getElapsedSeconds(lastPoint.timestamp, currentPoint.timestamp);

      if (
        distance >= TrackingPointComponent.OSRM_POINT_MIN_DISTANCE_METERS
        || elapsedSeconds >= TrackingPointComponent.OSRM_POINT_MIN_TIME_GAP_SECONDS
        || currentPoint.mockLocation !== lastPoint.mockLocation
      ) {
        compacted.push(currentPoint);
      }
    }

    compacted.push(points[points.length - 1]);
    return compacted;
  }

  private shouldAttemptOsrmMatch(points: OsrmRouteMatchPoint[]): boolean {
    if (points.length < 3) {
      return false;
    }

    const bounds = points.reduce(
      (accumulator, point) => ({
        minLat: Math.min(accumulator.minLat, point.lat),
        maxLat: Math.max(accumulator.maxLat, point.lat),
        minLng: Math.min(accumulator.minLng, point.lng),
        maxLng: Math.max(accumulator.maxLng, point.lng),
      }),
      {
        minLat: points[0].lat,
        maxLat: points[0].lat,
        minLng: points[0].lng,
        maxLng: points[0].lng,
      }
    );

    const diagonalDistance = this.calculateDistanceInMeters(
      { lat: bounds.minLat, lng: bounds.minLng },
      { lat: bounds.maxLat, lng: bounds.maxLng }
    );

    const usableAccuracy = points.every((point) =>
      this.normalizeGpsAccuracy(point.accuracy) <= TrackingPointComponent.KALMAN_MAX_ACCURACY_METERS
    );

    return usableAccuracy
      && diagonalDistance <= TrackingPointComponent.OSRM_MATCH_MAX_BBOX_DIAGONAL_METERS;
  }

  private getOsrmMatchRadius(point: OsrmRouteMatchPoint): number {
    return Math.round(Math.min(
      TrackingPointComponent.OSRM_MATCH_MAX_RADIUS_METERS,
      Math.max(
        TrackingPointComponent.OSRM_MATCH_MIN_RADIUS_METERS,
        this.normalizeGpsAccuracy(point.accuracy)
      )
    ));
  }

  private hasStrictlyIncreasingTimestamps(points: OsrmRouteMatchPoint[]): boolean {
    return points.every((point, index) => {
      if (!Number.isFinite(point.timestamp)) {
        return false;
      }
      return index === 0 || Number(point.timestamp) > Number(points[index - 1].timestamp);
    });
  }

  private normalizeGpsAccuracy(value: unknown): number {
    const accuracy = Number(value);
    if (!Number.isFinite(accuracy) || accuracy <= 0) {
      return TrackingPointComponent.KALMAN_DEFAULT_ACCURACY_METERS;
    }
    return Math.min(
      TrackingPointComponent.KALMAN_MAX_ACCURACY_METERS,
      Math.max(TrackingPointComponent.KALMAN_MIN_ACCURACY_METERS, accuracy)
    );
  }

  private getSourceGpsAccuracy(value: unknown): number {
    const accuracy = Number(value);
    return Number.isFinite(accuracy) && accuracy > 0
      ? accuracy
      : TrackingPointComponent.KALMAN_DEFAULT_ACCURACY_METERS;
  }

  private isPlausibleMatchedRoute(
    sourcePoints: OsrmRouteMatchPoint[],
    matchedRoute: L.LatLngExpression[],
    matchingConfidence: number | null = null
  ): boolean {
    if (sourcePoints.length < 2 || matchedRoute.length < 2) {
      return false;
    }

    const normalizedRoute = matchedRoute.map((point) => L.latLng(point));
    const startDistance = this.calculateDistanceInMeters(
      sourcePoints[0],
      { lat: normalizedRoute[0].lat, lng: normalizedRoute[0].lng }
    );
    const endDistance = this.calculateDistanceInMeters(
      sourcePoints[sourcePoints.length - 1],
      { lat: normalizedRoute[normalizedRoute.length - 1].lat, lng: normalizedRoute[normalizedRoute.length - 1].lng }
    );
    if (
      startDistance > TrackingPointComponent.MATCHED_ROUTE_ENDPOINT_TOLERANCE_METERS
      || endDistance > TrackingPointComponent.MATCHED_ROUTE_ENDPOINT_TOLERANCE_METERS
    ) {
      return false;
    }

    const rawDistance = this.calculatePathDistance(sourcePoints);
    const matchedDistance = this.calculatePathDistance(
      normalizedRoute.map((point) => ({ lat: point.lat, lng: point.lng }))
    );
    const hasPlausibleTotalDistance = rawDistance <= 0
      || matchedDistance <= Math.max(
        rawDistance * TrackingPointComponent.MATCHED_ROUTE_MAX_DISTANCE_FACTOR,
        rawDistance + 300
      );

    if (!hasPlausibleTotalDistance) {
      return false;
    }

    // Tight returns and parking/access-road maneuvers can legitimately travel
    // much farther than the straight line between two adjacent samples. When
    // OSRM itself reports a strong match, validate proximity to the complete
    // trace instead of rejecting the maneuver only because of that ratio.
    if (
      matchingConfidence !== null
      && matchingConfidence >= TrackingPointComponent.OSRM_HIGH_CONFIDENCE_MATCH
    ) {
      return this.hasPlausibleMatchedRouteCoverage(sourcePoints, normalizedRoute);
    }

    return this.hasPlausibleLocalRouteProgress(sourcePoints, normalizedRoute);
  }

  private hasPlausibleMatchedRouteCoverage(
    sourcePoints: OsrmRouteMatchPoint[],
    matchedRoute: L.LatLng[]
  ): boolean {
    if (!sourcePoints.length || !matchedRoute.length) {
      return false;
    }

    const coveredPoints = sourcePoints.filter((sourcePoint) => {
      const nearestDistance = matchedRoute.reduce(
        (minimum, routePoint) => Math.min(
          minimum,
          this.calculateDistanceInMeters(sourcePoint, routePoint)
        ),
        Number.POSITIVE_INFINITY
      );
      const allowedOffset = Math.min(
        TrackingPointComponent.MATCHED_ROUTE_MAX_SOURCE_OFFSET_METERS,
        Math.max(
          TrackingPointComponent.MATCHED_ROUTE_MIN_SOURCE_OFFSET_METERS,
          this.normalizeGpsAccuracy(sourcePoint.accuracy) * 2
        )
      );
      return nearestDistance <= allowedOffset;
    }).length;

    return coveredPoints / sourcePoints.length
      >= TrackingPointComponent.MATCHED_ROUTE_MIN_SOURCE_COVERAGE_RATIO;
  }

  private hasPlausibleOriginalTraceCoverage(
    sourcePoints: TrackingRouteRecord['points'],
    matchedRoute: L.LatLngExpression[]
  ): boolean {
    const normalizedRoute = matchedRoute.map((point) => L.latLng(point));
    const reliableSourcePoints = sourcePoints.filter((point) =>
      this.getSourceGpsAccuracy(point.accuracy)
        <= TrackingPointComponent.OSRM_MAX_SOURCE_ACCURACY_METERS
    );
    const pointsToValidate = reliableSourcePoints.length >= 2
      ? reliableSourcePoints
      : sourcePoints;

    if (!pointsToValidate.length || !normalizedRoute.length) {
      return false;
    }

    const coveredPoints = pointsToValidate.filter((sourcePoint) => {
      const nearestDistance = normalizedRoute.reduce(
        (minimum, routePoint) => Math.min(
          minimum,
          this.calculateDistanceInMeters(sourcePoint, routePoint)
        ),
        Number.POSITIVE_INFINITY
      );
      const allowedOffset = Math.min(
        TrackingPointComponent.MATCHED_ROUTE_ORIGINAL_TRACE_MAX_OFFSET_METERS,
        Math.max(
          TrackingPointComponent.MATCHED_ROUTE_ORIGINAL_TRACE_MIN_OFFSET_METERS,
          this.getSourceGpsAccuracy(sourcePoint.accuracy) * 1.75
        )
      );
      return nearestDistance <= allowedOffset;
    }).length;

    return coveredPoints / pointsToValidate.length
      >= TrackingPointComponent.MATCHED_ROUTE_MIN_ORIGINAL_COVERAGE_RATIO;
  }

  private hasPlausibleLocalRouteProgress(
    sourcePoints: OsrmRouteMatchPoint[],
    matchedRoute: L.LatLng[]
  ): boolean {
    if (sourcePoints.length < 2 || matchedRoute.length < 2) {
      return false;
    }

    const matchedIndexes: number[] = [];
    let searchStartIndex = 0;

    for (const sourcePoint of sourcePoints) {
      let nearestIndex = searchStartIndex;
      let nearestDistance = Number.POSITIVE_INFINITY;

      for (let index = searchStartIndex; index < matchedRoute.length; index++) {
        const distance = this.calculateDistanceInMeters(sourcePoint, matchedRoute[index]);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      }

      matchedIndexes.push(nearestIndex);
      searchStartIndex = nearestIndex;
    }

    for (let index = 1; index < sourcePoints.length; index++) {
      const directDistance = this.calculateDistanceInMeters(
        sourcePoints[index - 1],
        sourcePoints[index]
      );
      const fromIndex = matchedIndexes[index - 1];
      const toIndex = matchedIndexes[index];

      if (toIndex <= fromIndex) {
        continue;
      }

      const localRouteDistance = this.calculatePathDistance(
        matchedRoute
          .slice(fromIndex, toIndex + 1)
          .map((point) => ({ lat: point.lat, lng: point.lng }))
      );
      const maximumLocalDistance = Math.max(
        directDistance * TrackingPointComponent.MATCHED_ROUTE_MAX_LOCAL_DISTANCE_FACTOR,
        directDistance + TrackingPointComponent.MATCHED_ROUTE_MAX_LOCAL_EXTRA_METERS
      );

      if (localRouteDistance > maximumLocalDistance) {
        return false;
      }
    }

    return true;
  }

  private calculatePathDistance(points: Array<Pick<OsrmRouteMatchPoint, 'lat' | 'lng'>>): number {
    return points.slice(1).reduce((distance, point, index) =>
      distance + this.calculateDistanceInMeters(points[index], point), 0);
  }

  private shouldRenderRawPreview(points: TrackingRouteRecord['points']): boolean {
    const matchPoints = this.toRouteMatchPoints(points);
    if (matchPoints.length < 2) {
      return false;
    }

    // Only show the provisional raw line for compact local traces.
    // Wide/noisy traces should wait for OSRM so the user does not see a fake diagonal.
    return this.shouldAttemptOsrmMatch(matchPoints);
  }

  private calculateDistanceInMeters(
    from: Pick<OsrmRouteMatchPoint, 'lat' | 'lng'>,
    to: Pick<OsrmRouteMatchPoint, 'lat' | 'lng'>
  ): number {
    return L.latLng(from.lat, from.lng).distanceTo(L.latLng(to.lat, to.lng));
  }

  private buildSafeFallbackLatLngGroups(points: TrackingRouteRecord['points']): L.LatLngTuple[][] {
    if (points.length < 2) {
      return [];
    }

    const segments: L.LatLngTuple[][] = [];
    let currentSegment: L.LatLngTuple[] = [[points[0].lat, points[0].lng]];

    for (let index = 1; index < points.length; index++) {
      const previousPoint = points[index - 1];
      const currentPoint = points[index];

      if (!this.shouldKeepRawFallbackConnection(previousPoint, currentPoint)) {
        if (currentSegment.length >= 2) {
          segments.push(currentSegment);
        }
        currentSegment = [[currentPoint.lat, currentPoint.lng]];
        continue;
      }

      currentSegment.push([currentPoint.lat, currentPoint.lng]);
    }

    if (currentSegment.length >= 2) {
      segments.push(currentSegment);
    }

    return segments;
  }

  private buildSafeRouteMatchFallbackGroups(points: OsrmRouteMatchPoint[]): L.LatLngTuple[][] {
    if (points.length < 2) {
      return [];
    }

    const segments: L.LatLngTuple[][] = [];
    let currentSegment: L.LatLngTuple[] = [[points[0].lat, points[0].lng]];

    for (let index = 1; index < points.length; index++) {
      const previousPoint = points[index - 1];
      const currentPoint = points[index];

      if (!this.shouldKeepRawFallbackConnection(previousPoint, currentPoint)) {
        if (currentSegment.length >= 2) {
          segments.push(currentSegment);
        }
        currentSegment = [[currentPoint.lat, currentPoint.lng]];
        continue;
      }

      currentSegment.push([currentPoint.lat, currentPoint.lng]);
    }

    if (currentSegment.length >= 2) {
      segments.push(currentSegment);
    }

    return segments;
  }

  private buildUncoveredRouteMatchFallbackGroups(
    sourcePoints: OsrmRouteMatchPoint[],
    matchedRoute: L.LatLngExpression[]
  ): L.LatLngTuple[][] {
    if (sourcePoints.length < 3 || matchedRoute.length < 2) {
      return [];
    }

    const normalizedRoute = matchedRoute.map((point) => L.latLng(point));
    const uncoveredIndexes = sourcePoints
      .map((sourcePoint, index) => {
        const nearestDistance = normalizedRoute.reduce(
          (minimum, routePoint) => Math.min(
            minimum,
            this.calculateDistanceInMeters(sourcePoint, routePoint)
          ),
          Number.POSITIVE_INFINITY
        );
        const allowedOffset = Math.min(
          TrackingPointComponent.MATCHED_ROUTE_ORIGINAL_TRACE_MAX_OFFSET_METERS,
          Math.max(
            TrackingPointComponent.MATCHED_ROUTE_ORIGINAL_TRACE_MIN_OFFSET_METERS,
            this.normalizeGpsAccuracy(sourcePoint.accuracy) * 1.75
          )
        );
        return nearestDistance > allowedOffset ? index : -1;
      })
      .filter((index) => index >= 0);

    if (!uncoveredIndexes.length) {
      return [];
    }

    const fallbackGroups: L.LatLngTuple[][] = [];
    let runStart = uncoveredIndexes[0];
    let previousIndex = uncoveredIndexes[0];

    const appendRun = (startIndex: number, endIndex: number): void => {
      // A single displaced sample is usually GPS noise. Require at least
      // three consecutive samples before overriding OSRM locally.
      if (endIndex - startIndex + 1 < 3) {
        return;
      }
      const contextualStart = Math.max(0, startIndex - 1);
      const contextualEnd = Math.min(sourcePoints.length - 1, endIndex + 1);
      fallbackGroups.push(
        ...this.buildSafeRouteMatchFallbackGroups(
          sourcePoints.slice(contextualStart, contextualEnd + 1)
        )
      );
    };

    for (const currentIndex of uncoveredIndexes.slice(1)) {
      if (currentIndex === previousIndex + 1) {
        previousIndex = currentIndex;
        continue;
      }
      appendRun(runStart, previousIndex);
      runStart = currentIndex;
      previousIndex = currentIndex;
    }
    appendRun(runStart, previousIndex);

    return fallbackGroups.filter((group) => group.length >= 3);
  }

  private shouldKeepRawFallbackConnection(
    previousPoint: Pick<TrackingRouteRecord['points'][number], 'lat' | 'lng' | 'trackedAt' | 'mockLocation' | 'accuracy'>
      | Pick<OsrmRouteMatchPoint, 'lat' | 'lng' | 'timestamp' | 'mockLocation'>,
    currentPoint: Pick<TrackingRouteRecord['points'][number], 'lat' | 'lng' | 'trackedAt' | 'mockLocation' | 'accuracy'>
      | Pick<OsrmRouteMatchPoint, 'lat' | 'lng' | 'timestamp' | 'mockLocation'>
  ): boolean {
    if (previousPoint.mockLocation === true || currentPoint.mockLocation === true) {
      return false;
    }

    const distance = this.calculateDistanceInMeters(previousPoint, currentPoint);
    if (distance > TrackingPointComponent.SAFE_RAW_FALLBACK_MAX_DISTANCE_METERS) {
      return false;
    }

    const highestAccuracy = Math.max(
      this.resolvePointAccuracy(previousPoint),
      this.resolvePointAccuracy(currentPoint)
    );
    if (highestAccuracy > TrackingPointComponent.SAFE_RAW_FALLBACK_MAX_ACCURACY_METERS) {
      return false;
    }

    const previousTimestamp = this.resolveTrackingTimestamp(previousPoint);
    const currentTimestamp = this.resolveTrackingTimestamp(currentPoint);
    if (!previousTimestamp || !currentTimestamp || currentTimestamp <= previousTimestamp) {
      return true;
    }

    const elapsedSeconds = this.getElapsedSeconds(
      previousTimestamp,
      currentTimestamp
    );
    if (elapsedSeconds > TrackingPointComponent.ROUTE_SEGMENT_MAX_TIME_GAP_SECONDS) {
      return false;
    }

    const speedKmh = elapsedSeconds > 0 ? (distance / elapsedSeconds) * 3.6 : 0;
    return speedKmh <= TrackingPointComponent.ROUTE_SEGMENT_MAX_SPEED_KMH;
  }

  private getElapsedSeconds(
    previousTimestamp?: number | null,
    currentTimestamp?: number | null
  ): number {
    if (!previousTimestamp || !currentTimestamp || currentTimestamp <= previousTimestamp) {
      return 0;
    }

    return currentTimestamp - previousTimestamp;
  }

  private resolveTrackingTimestamp(
    point:
      | Pick<TrackingRouteRecord['points'][number], 'trackedAt'>
      | Pick<OsrmRouteMatchPoint, 'timestamp'>
  ): number | null {
    if ('timestamp' in point && typeof point.timestamp === 'number') {
      return point.timestamp;
    }

    if ('trackedAt' in point && point.trackedAt) {
      return Math.floor(new Date(point.trackedAt).getTime() / 1000);
    }

    return null;
  }

  private resolvePointAccuracy(
    point:
      | Pick<TrackingRouteRecord['points'][number], 'accuracy'>
      | Pick<OsrmRouteMatchPoint, 'lat'>
  ): number {
    return 'accuracy' in point && typeof point.accuracy === 'number'
      ? point.accuracy
      : 0;
  }

  getRouteColor(index: number): string {
    return TrackingPointComponent.ROUTE_COLORS[index % TrackingPointComponent.ROUTE_COLORS.length];
  }

  selectTimelinePoint(pointId: string): void {
    this.pausePlayback();
    this.selectedTimelinePointId = pointId;
    const selectedIndex = this.timelinePoints.findIndex((point) => point.id === pointId);
    if (selectedIndex >= 0) {
      this.playbackPosition = selectedIndex;
      this.updatePlaybackLabels(this.timelinePoints[selectedIndex]);
      this.movePlaybackMarkerToPoint(this.timelinePoints[selectedIndex]);
    }
    const marker = this.markerByPointId.get(pointId);
    if (!marker || !this.map) {
      this.scrollSelectedTimelineIntoView();
      return;
    }

    const latLng = marker.getLatLng();
    this.map.flyTo(latLng, Math.max(this.map.getZoom(), 16), { duration: 0.45 });
    marker.openPopup();
    this.scrollSelectedTimelineIntoView();
  }

  selectTimelinePointByIndex(index: number): void {
    const safeIndex = Math.round(Math.min(Math.max(Number(index), 0), Math.max(this.timelinePoints.length - 1, 0)));
    const point = this.timelinePoints[safeIndex];
    if (!point) {
      return;
    }

    this.selectTimelinePoint(point.id);
  }

  selectPreviousTimelinePoint(): void {
    this.pausePlayback();
    this.selectTimelinePointByIndex(this.selectedTimelineIndex - 1);
  }

  selectNextTimelinePoint(): void {
    this.pausePlayback();
    this.selectTimelinePointByIndex(this.selectedTimelineIndex + 1);
  }

  onTimelineSliderChange(index: number): void {
    this.pausePlayback();
    this.selectTimelinePointByIndex(index);
  }

  togglePlayback(): void {
    if (this.isPlaybackPlaying) {
      this.pausePlayback();
      return;
    }

    if (this.timelinePoints.length < 2 || typeof window === 'undefined') {
      return;
    }

    this.isLiveTracking = false;
    this.stopLiveTracking();
    if (this.playbackPosition >= this.timelinePoints.length - 1) {
      this.playbackPosition = 0;
      this.selectedTimelinePointId = this.timelinePoints[0].id;
    }

    const segmentIndex = Math.min(
      Math.floor(this.playbackPosition),
      this.timelinePoints.length - 2
    );
    this.isPlaybackPlaying = true;
    this.ensurePlaybackMarker(this.timelinePoints[segmentIndex]);
    this.animatePlaybackSegment(segmentIndex, this.playbackPosition - segmentIndex);
  }

  pausePlayback(): void {
    this.isPlaybackPlaying = false;
    if (this.playbackAnimationFrame !== null && typeof window !== 'undefined') {
      window.cancelAnimationFrame(this.playbackAnimationFrame);
      this.playbackAnimationFrame = null;
    }
  }

  setPlaybackRate(rate: number): void {
    if (!this.playbackRates.includes(rate)) {
      return;
    }
    const wasPlaying = this.isPlaybackPlaying;
    const currentPosition = this.playbackPosition;
    this.pausePlayback();
    this.playbackRate = rate;
    if (wasPlaying && currentPosition < this.timelinePoints.length - 1) {
      const segmentIndex = Math.floor(currentPosition);
      this.isPlaybackPlaying = true;
      this.animatePlaybackSegment(segmentIndex, currentPosition - segmentIndex);
    }
  }

  togglePlaybackFollow(): void {
    this.playbackFollowEnabled = !this.playbackFollowEnabled;
    if (this.playbackFollowEnabled && this.playbackMarker && this.map) {
      this.map.panTo(this.playbackMarker.getLatLng(), { animate: true, duration: 0.25 });
    }
  }

  get playbackProgressPercent(): number {
    return this.getTimelineProgress(this.playbackPosition);
  }

  private stopPlayback(removeMarker = false): void {
    this.pausePlayback();
    if (removeMarker && this.playbackMarker) {
      this.playbackMarker.remove();
      this.playbackMarker = null;
      this.playbackLastBearing = null;
    }
  }

  private animatePlaybackSegment(segmentIndex: number, initialProgress = 0): void {
    if (!this.isPlaybackPlaying || segmentIndex >= this.timelinePoints.length - 1 || typeof window === 'undefined') {
      this.finishPlayback();
      return;
    }

    const from = this.timelinePoints[segmentIndex];
    const to = this.timelinePoints[segmentIndex + 1];
    const fromLatLng = L.latLng(from.lat, from.lng);
    const toLatLng = L.latLng(to.lat, to.lng);
    const crossesDataGap = this.isRouteCutBetween(from.id, to.id);
    const playbackPathKey = this.getPlaybackPathKey(from.id, to.id);
    const processedPath = this.processedPlaybackPaths.get(playbackPathKey);
    const playbackProfile = this.playbackProfileByPath.get(playbackPathKey) || 'driving';
    const playbackPath = !crossesDataGap && processedPath?.length
      ? processedPath
      : [fromLatLng, toLatLng];
    const playbackPathDistances = playbackPath.slice(1).map((point, index) => playbackPath[index].distanceTo(point));
    const playbackPathTotalDistance = playbackPathDistances.reduce((total, distance) => total + distance, 0);
    const initialBearing = this.calculateBearing(playbackPath[0], playbackPath[1]);
    const duration = TrackingPointComponent.PLAYBACK_SEGMENT_DURATION_MS / this.playbackRate;
    const startedAt = performance.now() - (Math.min(1, Math.max(0, initialProgress)) * duration);
    this.ensurePlaybackMarker(from, playbackProfile);
    this.playbackLastBearing = initialBearing;
    this.playbackMarker?.setIcon(this.createPlaybackMarkerIcon(initialBearing, playbackProfile));

    const animate = (now: number): void => {
      if (!this.isPlaybackPlaying) {
        return;
      }

      const progress = Math.min(1, Math.max(0, (now - startedAt) / duration));
      const playbackState = crossesDataGap && progress < 1
        ? { position: fromLatLng, bearing: initialBearing }
        : this.interpolateAlongPath(playbackPath, playbackPathDistances, playbackPathTotalDistance, progress);
      const position = playbackState.position;
      if (this.shouldUpdatePlaybackBearing(playbackState.bearing)) {
        this.playbackLastBearing = playbackState.bearing;
        this.playbackMarker?.setIcon(this.createPlaybackMarkerIcon(playbackState.bearing, playbackProfile));
      }
      this.playbackMarker?.setLatLng(position);
      this.playbackPosition = segmentIndex + progress;
      this.updatePlaybackLabels(from, to, progress, crossesDataGap);
      this.followPlaybackMarker(position, now);

      if (progress >= 1) {
        this.selectedTimelinePointId = to.id;
        this.scrollSelectedTimelineIntoView();
        if (segmentIndex + 1 >= this.timelinePoints.length - 1) {
          this.finishPlayback();
          return;
        }
        this.animatePlaybackSegment(segmentIndex + 1);
        return;
      }

      this.playbackAnimationFrame = window.requestAnimationFrame(animate);
    };

    this.playbackAnimationFrame = window.requestAnimationFrame(animate);
  }

  private finishPlayback(): void {
    this.pausePlayback();
    const lastPoint = this.timelinePoints[this.timelinePoints.length - 1];
    if (!lastPoint) {
      return;
    }
    this.playbackPosition = this.timelinePoints.length - 1;
    this.selectedTimelinePointId = lastPoint.id;
    this.updatePlaybackLabels(lastPoint);
    this.playbackCurrentSpeedLabel = '0 km/h';
  }

  private ensurePlaybackMarker(
    point: TrackingTimelinePoint,
    profile: OsrmRoutingProfile = this.resolvePlaybackProfileForPoint(point.id)
  ): void {
    if (!this.map) {
      return;
    }
    if (!this.playbackMarker) {
      this.playbackMarker = L.marker([point.lat, point.lng], {
        icon: this.createPlaybackMarkerIcon(0, profile),
        keyboard: false,
        zIndexOffset: 1400,
      }).addTo(this.map);
    }
  }

  private movePlaybackMarkerToPoint(point: TrackingTimelinePoint): void {
    this.playbackMarker?.setLatLng([point.lat, point.lng]);
    this.playbackMarker?.setIcon(this.createPlaybackMarkerIcon(
      this.playbackLastBearing ?? 0,
      this.resolvePlaybackProfileForPoint(point.id)
    ));
  }

  private createPlaybackMarkerIcon(bearing: number, profile: OsrmRoutingProfile): L.DivIcon {
    const isFoot = profile === 'foot';
    const materialIcon = isFoot ? 'person_pin' : 'directions_car';
    const markerLabel = this.t(isFoot
      ? 'trackingPointsCatalog.map.playbackFootMarker'
      : 'trackingPointsCatalog.map.playbackDrivingMarker');

    return L.divIcon({
      className: `tracking-playback-marker tracking-playback-marker--${profile}`,
      html: `
        <span class="tracking-playback-marker__pulse"></span>
        <span class="tracking-playback-marker__heading" style="transform:rotate(${bearing}deg)">
          <i></i>
        </span>
        <span class="tracking-playback-marker__mode" title="${markerLabel}">
          <span class="tracking-playback-marker__mode-icon material-icons" aria-hidden="true">${materialIcon}</span>
        </span>
      `,
      iconSize: [44, 44],
      iconAnchor: [22, 22],
    });
  }

  private resolvePlaybackProfileForPoint(pointId: string): OsrmRoutingProfile {
    const pointIndex = this.timelinePoints.findIndex((point) => point.id === pointId);
    if (pointIndex >= 0 && pointIndex < this.timelinePoints.length - 1) {
      const nextPoint = this.timelinePoints[pointIndex + 1];
      const outgoingProfile = this.playbackProfileByPath.get(this.getPlaybackPathKey(pointId, nextPoint.id));
      if (outgoingProfile) {
        return outgoingProfile;
      }
    }
    if (pointIndex > 0) {
      const previousPoint = this.timelinePoints[pointIndex - 1];
      return this.playbackProfileByPath.get(this.getPlaybackPathKey(previousPoint.id, pointId)) || 'driving';
    }
    return 'driving';
  }

  private interpolateAlongPath(
    points: L.LatLng[],
    distances: number[],
    totalDistance: number,
    progress: number
  ): { position: L.LatLng; bearing: number } {
    if (points.length < 2 || totalDistance <= 0) {
      return {
        position: points[0] || L.latLng(this.defaultCenter),
        bearing: this.playbackLastBearing ?? 0,
      };
    }

    const targetDistance = Math.min(totalDistance, Math.max(0, progress) * totalDistance);
    let coveredDistance = 0;
    for (let index = 0; index < distances.length; index++) {
      const distance = distances[index];
      if (targetDistance <= coveredDistance + distance || index === distances.length - 1) {
        const ratio = distance > 0 ? (targetDistance - coveredDistance) / distance : 0;
        return {
          position: this.interpolateLatLng(points[index], points[index + 1], Math.min(1, Math.max(0, ratio))),
          bearing: this.calculateBearing(points[index], points[index + 1]),
        };
      }
      coveredDistance += distance;
    }

    return {
      position: points[points.length - 1],
      bearing: this.playbackLastBearing ?? 0,
    };
  }

  private shouldUpdatePlaybackBearing(nextBearing: number): boolean {
    if (this.playbackLastBearing === null) {
      return true;
    }
    const difference = Math.abs(((nextBearing - this.playbackLastBearing + 540) % 360) - 180);
    return difference >= 12;
  }

  private updatePlaybackLabels(
    from: TrackingTimelinePoint,
    to?: TrackingTimelinePoint,
    progress = 0,
    crossesDataGap = false
  ): void {
    if (!to) {
      this.playbackTimeLabel = from.timeLabel;
      this.playbackCurrentSpeedLabel = '-';
      return;
    }

    const fromTimestamp = new Date(from.trackedAt).getTime();
    const toTimestamp = new Date(to.trackedAt).getTime();
    if (Number.isFinite(fromTimestamp) && Number.isFinite(toTimestamp) && toTimestamp > fromTimestamp) {
      const currentTimestamp = fromTimestamp + ((toTimestamp - fromTimestamp) * progress);
      this.playbackTimeLabel = this.formatTime(new Date(currentTimestamp).toISOString());
      if (crossesDataGap) {
        this.playbackCurrentSpeedLabel = this.t('trackingPointsCatalog.map.playbackNoData');
        return;
      }
      const sourceRoutePoint = this.routeGroups
        .flatMap((group) => group.points)
        .find((point) => point.trackingPointId === from.id);
      const reportedSpeedKmh = sourceRoutePoint
        ? this.resolvePointSpeedKmh(sourceRoutePoint)
        : null;
      if (reportedSpeedKmh !== null) {
        this.playbackCurrentSpeedLabel = `${reportedSpeedKmh < 10 ? reportedSpeedKmh.toFixed(1) : reportedSpeedKmh.toFixed(0)} km/h`;
        return;
      }
      const distance = this.calculateDistanceInMeters(from, to);
      const speedKmh = (distance / ((toTimestamp - fromTimestamp) / 1000)) * 3.6;
      this.playbackCurrentSpeedLabel = `≈${speedKmh < 10 ? speedKmh.toFixed(1) : speedKmh.toFixed(0)} km/h`;
      return;
    }

    this.playbackTimeLabel = progress < 1 ? from.timeLabel : to.timeLabel;
    this.playbackCurrentSpeedLabel = '-';
  }

  private isRouteCutBetween(fromId: string, toId: string): boolean {
    for (const group of this.routeGroups) {
      const fromIndex = group.points.findIndex((point) => point.trackingPointId === fromId);
      if (fromIndex >= 0 && group.points[fromIndex + 1]?.trackingPointId === toId) {
        return this.shouldStartNewRouteSegment(group.points[fromIndex], group.points[fromIndex + 1]);
      }
    }
    return false;
  }

  private followPlaybackMarker(position: L.LatLng, now: number): void {
    if (!this.playbackFollowEnabled || !this.map || now - this.playbackLastCameraUpdate < 250) {
      return;
    }
    this.playbackLastCameraUpdate = now;
    this.map.panTo(position, { animate: true, duration: 0.25 });
  }

  trackTimelinePoint(_index: number, point: TrackingTimelinePoint): string {
    return point.id;
  }

  getTimelineProgress(index: number): number {
    if (this.timelinePoints.length <= 1) {
      return 0;
    }

    return (index / (this.timelinePoints.length - 1)) * 100;
  }

  private formatDateTime(value?: string | null): string {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const locale = this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX';
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  private formatDateTimeExact(value?: string | null): string {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const locale = this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX';
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(date);
  }

  private formatPointSpeed(
    point: TrackingRouteRecord['points'][number],
    routePoints: TrackingRouteRecord['points'] = []
  ): string {
    const reportedSpeedKmh = this.resolvePointSpeedKmh(point);
    const inferredSpeedKmh = reportedSpeedKmh === null
      ? this.inferPointSpeedKmh(point, routePoints)
      : null;
    const speedKmh = reportedSpeedKmh ?? inferredSpeedKmh;
    if (speedKmh === null) {
      return '-';
    }
    const locale = this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX';
    const formattedSpeed = `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(speedKmh)} km/h`;
    return reportedSpeedKmh !== null
      ? formattedSpeed
      : `≈${formattedSpeed} · ${this.t('trackingPointsCatalog.fields.speedEstimated')}`;
  }

  private formatBatteryLevel(value: unknown): string {
    const batteryLevel = Number(value);
    if (!Number.isFinite(batteryLevel) || batteryLevel < 0) {
      return '-';
    }
    return `${Math.round(Math.min(100, batteryLevel))}%`;
  }

  private buildBatteryIndicator(value: unknown): string {
    const batteryLevel = Number(value);
    if (!Number.isFinite(batteryLevel) || batteryLevel < 0) {
      return `<span class="tracking-route-popup__battery tracking-route-popup__battery--unknown"><span class="material-icons">battery_unknown</span>-</span>`;
    }
    const normalizedLevel = Math.round(Math.min(100, batteryLevel));
    const levelClass = normalizedLevel <= 15 ? 'critical' : normalizedLevel <= 35 ? 'low' : 'normal';
    const icon = normalizedLevel <= 15 ? 'battery_alert' : 'battery_std';
    return `<span class="tracking-route-popup__battery tracking-route-popup__battery--${levelClass}"><span class="material-icons">${icon}</span>${normalizedLevel}%</span>`;
  }

  private getPointBatteryLevel(point: TrackingRouteRecord['points'][number]): unknown {
    const sourcePoint = this.allPoints.find((item) => item.trackingPointId === point.trackingPointId);
    return sourcePoint?.rawPayload?.['batteryLevel'] ?? point.rawPayload?.['batteryLevel'];
  }

  private applyPointDetail(
    routePoint: TrackingRouteRecord['points'][number],
    detail: TrackingPointRecord
  ): void {
    // The battery level is supplied by the mobile app inside rawPayload.
    // Keep it on the route point so the popup does not depend on a second ID lookup.
    routePoint.rawPayload = detail.rawPayload || routePoint.rawPayload || null;
    routePoint.deviceId = detail.deviceId || routePoint.deviceId || null;
    routePoint.deviceVersion = detail.deviceVersion || routePoint.deviceVersion || null;
  }

  private buildRouteCutPopup(
    group: TrackingRouteRecord,
    previousPoint: TrackingRouteRecord['points'][number],
    currentPoint: TrackingRouteRecord['points'][number],
    cutId: string,
    durationLabel: string,
    distanceLabel: string
  ): string {
    return `
      <div class="tracking-route-popup">
        <span class="tracking-route-popup__tag tracking-route-popup__tag--cut">${cutId} · ${this.t('trackingPointsCatalog.map.cutTitle')}</span>
        <strong>${this.resolveRouteCutReason(previousPoint, currentPoint)}</strong>
        <strong>${this.t('trackingPointsCatalog.map.cutFrom')}</strong>
        <span>${this.t('trackingPointsCatalog.fields.trackedAt')}: ${this.formatDateTimeExact(previousPoint.trackedAt)}</span>
        <span>${this.t('trackingPointsCatalog.fields.speed')}: ${this.formatPointSpeed(previousPoint, group.points)}</span>
        <span class="tracking-route-popup__battery-row">${this.t('trackingPointsCatalog.fields.batteryLevel')}: ${this.buildBatteryIndicator(this.getPointBatteryLevel(previousPoint))}</span>
        <span>${this.t('trackingPointsCatalog.fields.pointId')}: ${previousPoint.trackingPointId || '-'}</span>
        <strong>${this.t('trackingPointsCatalog.map.cutTo')}</strong>
        <span>${this.t('trackingPointsCatalog.fields.trackedAt')}: ${this.formatDateTimeExact(currentPoint.trackedAt)}</span>
        <span>${this.t('trackingPointsCatalog.fields.speed')}: ${this.formatPointSpeed(currentPoint, group.points)}</span>
        <span class="tracking-route-popup__battery-row">${this.t('trackingPointsCatalog.fields.batteryLevel')}: ${this.buildBatteryIndicator(this.getPointBatteryLevel(currentPoint))}</span>
        <span>${this.t('trackingPointsCatalog.fields.pointId')}: ${currentPoint.trackingPointId || '-'}</span>
        <span>${this.t('trackingPointsCatalog.map.cutDuration')}: ${durationLabel}</span>
        <span>${this.t('trackingPointsCatalog.map.cutDistance')}: ${distanceLabel}</span>
      </div>
    `;
  }

  private async hydrateRouteCutPopup(
    group: TrackingRouteRecord,
    previousPoint: TrackingRouteRecord['points'][number],
    currentPoint: TrackingRouteRecord['points'][number],
    fromMarker: L.Marker,
    toMarker: L.Marker,
    buildPopup: () => string
  ): Promise<void> {
    const points = [previousPoint, currentPoint];
    const missingPoints = points.filter((point) => this.getPointBatteryLevel(point) === undefined);
    if (!missingPoints.length) {
      return;
    }

    await Promise.all(missingPoints.map(async (point) => {
      try {
        const detail = await firstValueFrom(
          this.apiService.get<TrackingPointRecord>(`attendance/tracking-points/${point.trackingPointId}`)
        );
        const normalizedDetail = this.normalizePoint(detail);
        this.applyPointDetail(point, normalizedDetail);
        const existingIndex = this.allPoints.findIndex((item) => item.trackingPointId === normalizedDetail.trackingPointId);
        if (existingIndex >= 0) {
          this.allPoints[existingIndex] = normalizedDetail;
        } else {
          this.allPoints.push(normalizedDetail);
        }
      } catch (error) {
        console.warn('Could not load battery level for route cut point.', point.trackingPointId, error);
      }
    }));

    const popup = buildPopup();
    fromMarker.setPopupContent(popup);
    toMarker.setPopupContent(popup);
    if (fromMarker.isPopupOpen()) {
      fromMarker.openPopup();
    } else if (toMarker.isPopupOpen()) {
      toMarker.openPopup();
    }
  }

  private inferPointSpeedKmh(
    point: TrackingRouteRecord['points'][number],
    routePoints: TrackingRouteRecord['points']
  ): number | null {
    const pointIndex = routePoints.findIndex((candidate) => candidate.trackingPointId === point.trackingPointId);
    if (pointIndex < 0) {
      return null;
    }

    const pairs: Array<[TrackingRouteRecord['points'][number], TrackingRouteRecord['points'][number]]> = [];
    if (pointIndex < routePoints.length - 1) {
      pairs.push([point, routePoints[pointIndex + 1]]);
    }
    if (pointIndex > 0) {
      pairs.push([routePoints[pointIndex - 1], point]);
    }

    for (const [from, to] of pairs) {
      const fromTimestamp = new Date(from.trackedAt).getTime();
      const toTimestamp = new Date(to.trackedAt).getTime();
      if (!Number.isFinite(fromTimestamp) || !Number.isFinite(toTimestamp) || toTimestamp <= fromTimestamp) {
        continue;
      }
      const elapsedSeconds = (toTimestamp - fromTimestamp) / 1000;
      const speedKmh = (this.calculateDistanceInMeters(from, to) / elapsedSeconds) * 3.6;
      if (
        Number.isFinite(speedKmh)
        && speedKmh >= 0
        && speedKmh <= TrackingPointComponent.ROUTE_SEGMENT_MAX_SPEED_KMH
      ) {
        return speedKmh;
      }
    }

    return null;
  }

  private formatTime(value?: string | null): string {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const locale = this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX';
    return new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  private getActiveEmployeeId(): string {
    return (this.activeFilters['employee'] as string) || '';
  }

  private getActiveCompanyId(): string { return (this.activeFilters['company'] as string) || ''; }

  private canSearchTrackingPoints(): boolean {
    return !!this.getActiveEmployeeId() && !!this.dateFrom;
  }

  private getEmployeeSearchText(value: string | EmployeeRecord | null | undefined): string {
    if (!value) {
      return '';
    }

    if (typeof value === 'string') {
      return value;
    }

    return value.fullName || value.employeeNumber || '';
  }

  private restoreFilterState(): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }

    try {
      const rawState = sessionStorage.getItem(TrackingPointComponent.FILTER_STATE_KEY);
      if (!rawState) {
        return;
      }

      const state = JSON.parse(rawState) as Partial<TrackingPointsFilterState>;
      this.activeFilters = state.activeFilters || {};
      this.dateFrom = typeof state.dateFrom === 'string' ? state.dateFrom : '';
      this.dateTo = this.dateFrom;
      this.activeFilters = {
        ...this.activeFilters,
        dateFrom: this.dateFrom || null,
        dateTo: this.dateFrom || null,
      };
      this.employeeSearchText = typeof state.employeeSearchText === 'string' ? state.employeeSearchText : '';
      this.currentPage = typeof state.currentPage === 'number' && state.currentPage > 0 ? state.currentPage : 1;
      this.pageSize = typeof state.pageSize === 'number' && state.pageSize > 0 ? state.pageSize : this.pageSize;
    } catch {
      this.clearPersistedFilterState();
    }
  }

  private persistFilterState(): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }

    const state: TrackingPointsFilterState = {
      activeFilters: { ...this.activeFilters },
      dateFrom: this.dateFrom,
      dateTo: this.dateTo,
      employeeSearchText: this.getEmployeeSearchText(this.employeeSearchText),
      currentPage: this.currentPage,
      pageSize: this.pageSize,
    };
    sessionStorage.setItem(TrackingPointComponent.FILTER_STATE_KEY, JSON.stringify(state));
  }

  private clearPersistedFilterState(): void {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(TrackingPointComponent.FILTER_STATE_KEY);
    }
  }

  private resetTrackingWorkspace(): void {
    this.stopPlayback(true);
    this.allPoints = [];
    this.dataSource.data = [];
    this.totalItems = 0;
    this.routeGroups = [];
    this.timelinePoints = [];
    this.selectedTimelinePointId = null;
    this.playbackPosition = 0;
    this.playbackTimeLabel = '-';
    this.playbackCurrentSpeedLabel = '-';
    this.routeSummary = {
      startLabel: '-',
      endLabel: '-',
      durationLabel: '-',
      distanceLabel: '-',
      totalPointsLabel: '0',
      totalPoints: 0,
    };
    this.renderRoutesOnMap();
  }

  private buildDateRangeParams(): Record<string, string> {
    if (!this.dateFrom) {
      return {};
    }

    return {
      dateFrom: this.toDayStartIso(this.dateFrom),
      dateTo: this.toDayEndIso(this.dateFrom),
    };
  }

  private toDayStartIso(value: string): string {
    return new Date(`${value}T00:00:00`).toISOString();
  }

  private toDayEndIso(value: string): string {
    return new Date(`${value}T23:59:59.999`).toISOString();
  }

  private hasAccess(action: string): boolean {
    return this.authorizationService.hasAccess({ resource: 'tracking', action });
  }

  private rebuildRoutePresentation(): void {
    const group = this.routeGroups[0];
    if (!group?.points?.length) {
      this.stopPlayback(true);
      this.timelinePoints = [];
      this.selectedTimelinePointId = null;
      this.playbackPosition = 0;
      this.playbackTimeLabel = '-';
      this.playbackCurrentSpeedLabel = '-';
      this.routeSummary = {
        startLabel: '-',
        endLabel: '-',
        durationLabel: '-',
        distanceLabel: '-',
        totalPointsLabel: '0',
        totalPoints: 0,
      };
      return;
    }

    this.timelinePoints = group.points.map((point, index) => {
      const sourcePoint = this.allPoints.find((item) => item.trackingPointId === point.trackingPointId);
      const isStart = index === 0;
      const isEnd = index === group.points.length - 1;

      return {
        id: point.trackingPointId,
        employeeNumber: group.employeeNumber,
        trackedAt: point.trackedAt,
        timeLabel: this.formatTime(point.trackedAt),
        dateTimeLabel: this.formatDateTime(point.trackedAt),
        coordinatesLabel: `${point.lat.toFixed(6)}, ${point.lng.toFixed(6)}`,
        accuracyLabel: `${sourcePoint?.accuracy ?? point.accuracy ?? '-'}`,
        addressSummary: this.extractAddressSummary(sourcePoint?.rawPayload || null),
        statusLabel: this.resolvePointStatus(sourcePoint, point),
        deviceLabel: sourcePoint?.deviceId || point.deviceId || sourcePoint?.deviceVersion || point.deviceVersion || null,
        outsideWorkdayLabel: sourcePoint?.outsideWorkday || point.outsideWorkday ? this.t('trackingPointsCatalog.fields.outsideWorkday') : null,
        lat: point.lat,
        lng: point.lng,
        isStart,
        isEnd,
      };
    });

    this.routeSummary = this.buildRouteSummary(group.points);

    if (!this.selectedTimelinePointId || !this.timelinePoints.some((point) => point.id === this.selectedTimelinePointId)) {
      this.selectedTimelinePointId = this.timelinePoints[0]?.id || null;
    }

    if (!this.isPlaybackPlaying) {
      this.playbackPosition = this.selectedTimelineIndex;
      const selectedPoint = this.timelinePoints[this.selectedTimelineIndex];
      if (selectedPoint) {
        this.updatePlaybackLabels(selectedPoint);
      }
    }

    this.scrollSelectedTimelineIntoView();
  }

  private buildRouteSummary(points: TrackingRouteRecord['points']): TrackingRouteSummary {
    const first = points[0];
    const last = points[points.length - 1];
    const totalDistanceMeters = points.reduce((total, currentPoint, index) => {
      if (index === 0) {
        return total;
      }

      return total + this.calculateDistanceMeters(points[index - 1], currentPoint);
    }, 0);

    return {
      startLabel: this.formatTime(first?.trackedAt),
      endLabel: this.formatTime(last?.trackedAt),
      durationLabel: this.formatDuration(first?.trackedAt, last?.trackedAt),
      distanceLabel: this.formatDistance(totalDistanceMeters),
      totalPointsLabel: new Intl.NumberFormat(this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX').format(points.length),
      totalPoints: points.length,
    };
  }

  private formatDuration(start?: string | null, end?: string | null): string {
    if (!start || !end) {
      return '-';
    }

    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffMs = endDate.getTime() - startDate.getTime();

    if (!Number.isFinite(diffMs) || diffMs < 0) {
      return '-';
    }

    const totalMinutes = Math.round(diffMs / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours <= 0) {
      return `${minutes} min`;
    }

    if (minutes <= 0) {
      return `${hours} h`;
    }

    return `${hours} h ${minutes} min`;
  }

  private formatDistance(distanceMeters: number): string {
    if (!Number.isFinite(distanceMeters) || distanceMeters <= 0) {
      return '0 m';
    }

    if (distanceMeters < 1000) {
      return `${Math.round(distanceMeters)} m`;
    }

    const distanceKm = distanceMeters / 1000;
    return `${distanceKm >= 10 ? distanceKm.toFixed(0) : distanceKm.toFixed(1)} km`;
  }

  private calculateDistanceMeters(
    startPoint: Pick<TrackingRouteRecord['points'][number], 'lat' | 'lng'>,
    endPoint: Pick<TrackingRouteRecord['points'][number], 'lat' | 'lng'>
  ): number {
    const earthRadius = 6371000;
    const lat1 = this.toRadians(startPoint.lat);
    const lat2 = this.toRadians(endPoint.lat);
    const deltaLat = this.toRadians(endPoint.lat - startPoint.lat);
    const deltaLng = this.toRadians(endPoint.lng - startPoint.lng);

    const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2)
      + Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return earthRadius * c;
  }

  private toRadians(value: number): number {
    return value * (Math.PI / 180);
  }

  private createEndpointIcon(
    type: 'start' | 'end',
    color: string,
    trackedAt?: string | null
  ): L.DivIcon {
    const isStart = type === 'start';
    const label = this.t(isStart
      ? 'trackingPointsCatalog.map.timelineStart'
      : 'trackingPointsCatalog.map.timelineEnd');
    const time = this.formatTime(trackedAt);

    return L.divIcon({
      className: 'tracking-points-marker',
      html: `
        <span class="tracking-points-marker__endpoint tracking-points-marker__endpoint--${type}" style="--route-color:${color}">
          <span class="tracking-points-marker__pin tracking-points-marker__pin--${type}">
            <span class="tracking-points-marker__glyph">${isStart ? 'A' : 'B'}</span>
          </span>
          <span class="tracking-points-marker__label">
            <strong>${label}</strong>
            ${time && time !== '-' ? `<small>${time}</small>` : ''}
          </span>
        </span>
      `,
      iconSize: [116, 44],
      iconAnchor: isStart ? [18, 22] : [98, 22],
      popupAnchor: isStart ? [0, -22] : [0, -22],
    });
  }

  private createMockLocationIcon(): L.DivIcon {
    return L.divIcon({
      className: 'tracking-points-mock-location-marker',
      html: '<span class="tracking-points-mock-location-marker__badge" title="Ubicaci&oacute;n simulada">&#9888;</span>',
      iconSize: [24, 24],
      iconAnchor: [2, 24],
    });
  }

  private isMockLocation(
    point: Pick<TrackingRouteRecord['points'][number], 'mockLocation'>,
    sourcePoint?: TrackingPointRecord
  ): boolean {
    return point.mockLocation === true
      || sourcePoint?.mockLocation === true
      || sourcePoint?.rawPayload?.['mockLocation'] === true;
  }

  private buildPointPopup(group: TrackingRouteRecord, point: TrackingRouteRecord['points'][number], isStart: boolean, isEnd: boolean): string {
    const sourcePoint = this.allPoints.find((item) => item.trackingPointId === point.trackingPointId);
    const timelinePoint = this.timelinePoints.find((item) => item.id === point.trackingPointId);
    const pointTag = isStart
      ? this.t('trackingPointsCatalog.map.timelineStart')
      : isEnd
        ? this.t('trackingPointsCatalog.map.timelineEnd')
        : this.formatTime(point.trackedAt);

    return `
      <div class="tracking-route-popup">
        <span class="tracking-route-popup__tag">${pointTag}</span>
        <strong>${group.employeeNumber}</strong>
        <span>${this.formatDateTimeExact(point.trackedAt)}</span>
        <span>${this.t('trackingPointsCatalog.fields.speed')}: ${this.formatPointSpeed(point, group.points)}</span>
        <span class="tracking-route-popup__battery-row">${this.t('trackingPointsCatalog.fields.batteryLevel')}: ${this.buildBatteryIndicator(this.getPointBatteryLevel(point))}</span>
        <span>${this.t('trackingPointsCatalog.fields.pointId')}: ${point.trackingPointId || '-'}</span>
        <span>${this.t('trackingPointsCatalog.fields.coordinates')}: ${point.lat.toFixed(6)}, ${point.lng.toFixed(6)}</span>
        <span>${this.t('trackingPointsCatalog.fields.accuracy')}: ${sourcePoint?.accuracy ?? point.accuracy ?? '-'}</span>
        ${timelinePoint?.addressSummary ? `<span>${timelinePoint.addressSummary}</span>` : ''}
        ${timelinePoint?.statusLabel ? `<span>${timelinePoint.statusLabel}</span>` : ''}
        ${this.isMockLocation(point, sourcePoint) ? '<span class="tracking-route-popup__mock-location">⚠ Ubicación simulada detectada</span>' : ''}
        ${(sourcePoint?.deviceId || point.deviceId) ? `<span>${this.t('trackingPointsCatalog.fields.deviceId')}: ${sourcePoint?.deviceId || point.deviceId}</span>` : ''}
      </div>
    `;
  }

  private async hydratePointPopup(
    group: TrackingRouteRecord,
    point: TrackingRouteRecord['points'][number],
    marker: L.Marker | L.CircleMarker,
    isStart: boolean,
    isEnd: boolean
  ): Promise<void> {
    const existingPoint = this.allPoints.find((item) => item.trackingPointId === point.trackingPointId);
    if (existingPoint?.rawPayload?.['batteryLevel'] !== undefined || point.rawPayload?.['batteryLevel'] !== undefined) {
      return;
    }

    try {
      const detail = await firstValueFrom(
        this.apiService.get<TrackingPointRecord>(`attendance/tracking-points/${point.trackingPointId}`)
      );
      const normalizedDetail = this.normalizePoint(detail);
      this.applyPointDetail(point, normalizedDetail);
      const existingIndex = this.allPoints.findIndex((item) => item.trackingPointId === normalizedDetail.trackingPointId);
      if (existingIndex >= 0) {
        this.allPoints[existingIndex] = normalizedDetail;
      } else {
        this.allPoints.push(normalizedDetail);
      }
      marker.setPopupContent(this.buildPointPopup(group, point, isStart, isEnd));
      if (marker.isPopupOpen()) {
        marker.openPopup();
      }
    } catch (error) {
      console.warn('Could not load complete tracking point metadata.', error);
    }
  }

  private resolvePointStatus(sourcePoint: TrackingPointRecord | undefined, point: TrackingRouteRecord['points'][number]): string | null {
    if (sourcePoint?.outsideWorkday || point.outsideWorkday) {
      return this.t('trackingPointsCatalog.fields.outsideWorkday');
    }

    if (sourcePoint && sourcePoint.isActive === false) {
      return this.t('common.status.inactive');
    }

    return null;
  }

  private extractAddressSummary(payload: Record<string, unknown> | null): string | null {
    if (!payload) {
      return null;
    }

    const directKeys = ['address', 'formattedAddress', 'locationName', 'reference', 'placeName'];
    for (const key of directKeys) {
      const value = payload[key];
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }

    const nestedObjects = Object.values(payload).filter((value): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value));
    for (const nested of nestedObjects) {
      for (const key of directKeys) {
        const value = nested[key];
        if (typeof value === 'string' && value.trim()) {
          return value.trim();
        }
      }
    }

    return null;
  }

  private onMapPointSelected(pointId: string): void {
    this.pausePlayback();
    this.selectedTimelinePointId = pointId;
    const selectedIndex = this.timelinePoints.findIndex((point) => point.id === pointId);
    if (selectedIndex >= 0) {
      this.playbackPosition = selectedIndex;
      this.updatePlaybackLabels(this.timelinePoints[selectedIndex]);
      this.movePlaybackMarkerToPoint(this.timelinePoints[selectedIndex]);
    }
    this.scrollSelectedTimelineIntoView();
  }

  private scrollSelectedTimelineIntoView(): void {
    if (!this.selectedTimelinePointId) {
      return;
    }

    setTimeout(() => {
      const selectedElement = this.timelineItemRefs?.find((item) => item.nativeElement.dataset['pointId'] === this.selectedTimelinePointId);
      selectedElement?.nativeElement.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      });
    }, 0);
  }

  private showNotification(
    colorName: string,
    text: string,
    placementFrom: MatSnackBarVerticalPosition,
    placementAlign: MatSnackBarHorizontalPosition
  ): void {
    this.snackBar.open(text, '', {
      duration: 2200,
      verticalPosition: placementFrom,
      horizontalPosition: placementAlign,
      panelClass: colorName,
    });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }
}



